import { Alpine } from '../core/alpine-core.js';
import { viteEnv } from '../core/import-meta-env.js';
import { preferredAppOrigin } from '../core/preferred-app-origin.js';
import { apiFetch, getAccessToken } from './api.js';
import QRCode from 'qrcode';
import { mountAdminDatetime, dateToDatetimeLocalString, parseToDate } from './datetime-flatpickr.js';
import {
    loadScorerQuickConfig,
    mergeMoreQuickPresets,
    quickPresetButtonLabel,
    withNearCheckoutNsOnFourthQuickTop,
} from './scorer-quick-config.js';
import { venuePersonaAvatarDataUri } from '../public/venue-kiosk-avatars.js';
import {
    canUseBrowserMediaDevices,
    mediaDevicesBlockedReason,
    buildVideoConstraints,
    openKioskMedia,
    closeVenuePcBridge,
    resolveKioskCameraSource,
    normalizeKioskCameraSource,
    localStreamReadyForSource,
    fetchVenueCameraHostStatus,
    readSavedKioskCameraSource,
    persistKioskCameraSource,
    ensureKioskMicTrack,
} from './match-media-session.js';
import {
    fetchActiveBroadcastSession,
    startBroadcastSession,
    ensureBroadcastSessionForMatch,
    stopBroadcastSession,
    buildSpectatorWatchUrl,
    ensureBroadcastHostWindow,
    waitForBroadcastHostCapturing,
    fetchBroadcastHostStatus,
    fetchBroadcastSettings,
    setBroadcastYoutubeEnabled,
    requestBroadcastHostWake,
} from '../shared/venue-broadcast-client.js';
import { isVenueAdminPinConfigured, verifyVenueAdminPin } from '../shared/go-live-pin.js';
import { checkoutRuleShortLabel, startInRuleShortLabel } from '../shared/match-format-rule-labels.js';

function fmtDateTime(value) {
    if (!value) return 'TBC';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return 'TBC';
    return d.toLocaleString('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function csvToNumbers(value) {
    return String(value || '')
        .split(',')
        .map((v) => Number(String(v).trim()))
        .filter((v) => Number.isFinite(v) && v > 0);
}

function csvToStrings(value) {
    return String(value || '')
        .split(',')
        .map((v) => String(v).trim())
        .filter(Boolean);
}

function playerName(player) {
    if (!player) return 'Player';
    if (player.isGuest) return player.fullName || 'Guest';
    if (player.nickname) return `${player.fullName} (${player.nickname})`;
    return player.fullName || 'Player';
}

function rosterPlayerIdFromSession(sessionStore, metaCurrentPlayerId = '') {
    const metaId = String(metaCurrentPlayerId || '').trim();
    if (metaId) return metaId;
    const store = sessionStore || {};
    const me = store.me || {};
    return String(
        store.rosterSelfPlayerId
        || me.playerId
        || me._id
        || '',
    ).trim();
}

const CHALLENGE_SCORER_QUICK_LS = 'pdl.challenge-scorer.quick';

const GUEST_RANDOM_NAMES = [
    'Ace Morgan', 'Bullseye Bill', 'Treble Tess', 'Shanghai Sue', 'Robin Hood', 'Oche Annie', 'Flight Path Pete',
    'Double Top Dan', 'Checkout Charlie', 'Ton-Eighty Tom', 'Barrel Bob', 'Wire Tap Walt', 'Black Dog Dave',
    'Corkscrew Casey', 'Feather Finley', 'Dartboard Dana', 'Sawdust Sam', 'Chalk Dust Chloe', 'Leg Shot Lou',
    'Bounce Out Benny', 'Wire Hanger Hank', 'Bed & Breakfast Bea', 'Madhouse Mira', 'Ton Machine Mike',
    'Spider Legs Spencer', 'Rubberband Rita', 'Clock Strikes Clara', 'Yardstick Yuri', 'Hammer Time Hannah',
    'Split Decision Sid', 'Cover Shot Cole', 'Fish Eye Finn', 'Red Wire Riley', 'Green Room Gwen',
    'Ice Breaker Izzy', 'Hot Wire Hugo', 'Cold Steel Stella', 'Copper Line Cal', 'Silver Tongue Sky',
    'Gold Flake Gary', 'Platinum Pat', 'Bronze Medal Mo', 'Steel Tip Steve', 'Soft Tip Sophie',
    'Flight School Frank', 'Landing Strip Lana', 'Bull Ring Bruno', 'Outer Ring Olive', 'Inner Ring Ira',
    'Match Play Manny', 'Leggy Larry', 'Set Point Selena', 'Break Shot Brenda',
];

function newGuestAvatarSeed() {
    try {
        return crypto.randomUUID();
    } catch {
        return `guest-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    }
}

function pickRandomGuestName(excludeName = '') {
    const exclude = String(excludeName || '').trim().toLowerCase();
    const pool = [...GUEST_RANDOM_NAMES];
    for (let t = 0; t < 40; t++) {
        const name = pool[Math.floor(Math.random() * pool.length)];
        if (String(name).trim().toLowerCase() !== exclude) return name;
    }
    return `Guest ${100 + Math.floor(Math.random() * 899)}`;
}

function buildGuestAvatarChoices(currentSeed) {
    const cur = String(currentSeed || newGuestAvatarSeed());
    const out = [{ seed: cur, url: venuePersonaAvatarDataUri(cur) }];
    const seen = new Set([cur]);
    let guard = 0;
    while (out.length < 8 && guard < 400) {
        guard += 1;
        const s = newGuestAvatarSeed();
        if (seen.has(s)) continue;
        seen.add(s);
        out.push({ seed: s, url: venuePersonaAvatarDataUri(s) });
    }
    return out;
}

function scoreRowSeed(player) {
    return {
        playerId: String(player?._id || player?.playerId || ''),
        player: player || null,
        legsWon: Number(player?.legsWon) || 0,
        pointsScored: Number(player?.pointsScored) || 0,
        dartsThrown: Number(player?.dartsThrown) || 0,
        highestScore: Number(player?.highestScore) || 0,
        tonsCsv: Array.isArray(player?.tons) ? player.tons.join(', ') : '',
        checkoutsCsv: Array.isArray(player?.checkouts) ? player.checkouts.join(', ') : '',
        notes: String(player?.notes || ''),
    };
}

function challengeIdFromPath() {
    const parts = location.pathname.split('/').filter(Boolean);
    let idx = parts.indexOf('challenge-lobby');
    if (idx >= 0 && parts[idx + 1]) return decodeURIComponent(parts[idx + 1]);
    idx = parts.indexOf('challenge-score');
    if (idx >= 0 && parts[idx + 1]) return decodeURIComponent(parts[idx + 1]);
    idx = parts.indexOf('challenges');
    if (idx < 0 || !parts[idx + 1]) return '';
    return decodeURIComponent(parts[idx + 1]);
}

/** 'console' | 'matchConsole' | 'lobby' | 'scorer' — admin or venue-kiosk embed routes */
function challengeSurfaceFromPath() {
    const parts = location.pathname.split('/').filter(Boolean);
    const lobbyIdx = parts.indexOf('challenge-lobby');
    if (lobbyIdx >= 0 && parts[lobbyIdx + 1]) return 'lobby';
    const kioskIdx = parts.indexOf('challenge-score');
    if (kioskIdx >= 0 && parts[kioskIdx + 1]) return 'matchConsole';
    const idx = parts.indexOf('challenges');
    if (idx < 0 || !parts[idx + 1]) return 'console';
    const tail = parts[idx + 2];
    if (tail === 'scorer') return 'scorer';
    if (tail === 'score') return 'matchConsole';
    return 'console';
}

function routerNavigateChallenge(path) {
    const R = window.PineconeRouter;
    if (R?.navigate) R.navigate(path);
    else window.location.href = path;
}

function makeChallengeScoringDeviceId(challengeId) {
    try {
        const key = `pdl.challenge-scorer-id.${challengeId}`;
        const existing = window.localStorage.getItem(key);
        if (existing) return existing;
        const created = `challenge-${challengeId}-${Math.random().toString(36).slice(2, 10)}`;
        window.localStorage.setItem(key, created);
        return created;
    } catch {
        return `challenge-${challengeId}-scorer`;
    }
}

function challengeQueryParam(name) {
    try {
        return new URLSearchParams(window.location.search).get(name) || '';
    } catch {
        return '';
    }
}

function applyDedicatedChromeMode(on) {
    try {
        window.Alpine?.store?.('ui')?.setHideChrome?.(!!on);
        const shell = document.getElementById('admin');
        if (shell) shell.classList.toggle('layout-scorer-bleed', !!on);
    } catch { }
}

function statusMeta(status) {
    const map = {
        pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
        accepted: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
        in_progress: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
        submitted: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
        confirmed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
        disputed: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
        declined: 'bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200',
        cancelled: 'bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200',
    };
    return map[status] || map.pending;
}

function modeMeta(mode) {
    return mode === 'remote'
        ? { label: 'Remote Camera Challenge', icon: 'fa-globe' }
        : { label: 'Local Venue Challenge', icon: 'fa-location-dot' };
}

const OUT_SUGGESTIONS = {
    170: ['T20 T20 Bull'],
    167: ['T20 T19 Bull'],
    164: ['T20 T18 Bull'],
    161: ['T20 T17 Bull'],
    160: ['T20 T20 D20'],
    158: ['T20 T20 D19'],
    157: ['T20 T19 D20'],
    156: ['T20 T20 D18'],
    155: ['T20 T19 D19'],
    154: ['T20 T18 D20'],
    153: ['T20 T19 D18'],
    152: ['T20 T20 D16'],
    151: ['T20 T17 D20'],
    150: ['T20 T18 D18'],
    149: ['T20 T19 D16'],
    148: ['T20 T16 D20'],
    147: ['T20 T17 D18'],
    146: ['T20 T18 D16'],
    145: ['T20 T15 D20'],
    144: ['T20 T20 D12'],
    143: ['T20 T17 D16'],
    142: ['T20 T14 D20'],
    141: ['T20 T19 D12'],
    140: ['T20 T20 D10'],
    139: ['T19 T14 D20'],
    138: ['T20 T18 D12'],
    137: ['T19 T16 D16'],
    136: ['T20 T20 D8'],
    135: ['Bull T15 D20'],
    134: ['T20 T14 D16'],
    133: ['T20 T19 D8'],
    132: ['Bull Bull D16', 'T20 T16 D12'],
    131: ['T20 T13 D16'],
    130: ['T20 T20 D5'],
    129: ['T19 T16 D12'],
    128: ['T18 T14 D16'],
    127: ['T20 T17 D8'],
    126: ['T19 T19 D6'],
    125: ['Bull T15 D15', '25 T20 Bull'],
    124: ['T20 T16 D8'],
    123: ['T19 T16 D9'],
    122: ['T18 T18 D7'],
    121: ['T20 T11 D14'],
    120: ['T20 20 D20'],
    119: ['T19 T12 D13'],
    118: ['T20 18 D20'],
    117: ['T20 17 D20'],
    116: ['T20 16 D20'],
    115: ['T20 15 D20'],
    114: ['T20 14 D20'],
    113: ['T20 13 D20'],
    112: ['T20 12 D20'],
    111: ['T20 11 D20'],
    110: ['T20 10 D20'],
    109: ['T20 9 D20'],
    108: ['T20 16 D16'],
    107: ['T19 10 D20'],
    106: ['T20 6 D20'],
    105: ['T20 13 D16'],
    104: ['T18 18 D16'],
    103: ['T19 10 D18'],
    102: ['T20 10 D16'],
    101: ['T17 10 D20'],
    100: ['T20 D20'],
    99: ['T19 10 D16'],
    98: ['T20 D19'],
    97: ['T19 D20'],
    96: ['T20 D18'],
    95: ['T19 D19'],
    94: ['T18 D20'],
    93: ['T19 D18'],
    92: ['T20 D16'],
    91: ['T17 D20'],
    90: ['T18 D18'],
    89: ['T19 D16'],
    88: ['T16 D20'],
    87: ['T17 D18'],
    86: ['T18 D16'],
    85: ['T15 D20'],
    84: ['T20 D12'],
    83: ['T17 D16'],
    82: ['Bull D16', 'T14 D20'],
    81: ['T19 D12'],
    80: ['T20 D10'],
    79: ['T19 D11'],
    78: ['T18 D12'],
    77: ['T19 D10'],
    76: ['T20 D8'],
    75: ['T17 D12'],
    74: ['T14 D16'],
    73: ['T19 D8'],
    72: ['T16 D12'],
    71: ['T13 D16'],
    70: ['T18 D8'],
    69: ['T19 D6'],
    68: ['T20 D4'],
    67: ['T17 D8'],
    66: ['T10 D18'],
    65: ['25 D20'],
    64: ['T16 D8'],
    63: ['T13 D12'],
    62: ['T10 D16'],
    61: ['T15 D8'],
    60: ['20 D20'],
    59: ['19 D20'],
    58: ['18 D20'],
    57: ['17 D20'],
    56: ['16 D20'],
    55: ['15 D20'],
    54: ['14 D20'],
    53: ['13 D20'],
    52: ['12 D20'],
    51: ['11 D20'],
    50: ['10 D20', 'Bull'],
    49: ['9 D20'],
    48: ['16 D16'],
    47: ['15 D16'],
    46: ['6 D20'],
    45: ['13 D16'],
    44: ['12 D16'],
    43: ['11 D16'],
    42: ['10 D16'],
    41: ['9 D16'],
    40: ['D20'],
    39: ['7 D16'],
    38: ['D19'],
    37: ['5 D16'],
    36: ['D18'],
    35: ['3 D16'],
    34: ['D17'],
    33: ['1 D16'],
    32: ['D16'],
    31: ['15 D8'],
    30: ['D15'],
    29: ['13 D8'],
    28: ['D14'],
    27: ['11 D8'],
    26: ['D13'],
    25: ['9 D8'],
    24: ['D12'],
    23: ['7 D8'],
    22: ['D11'],
    21: ['5 D8'],
    20: ['D10'],
    19: ['3 D8'],
    18: ['D9'],
    17: ['1 D8'],
    16: ['D8'],
    15: ['7 D4'],
    14: ['D7'],
    13: ['5 D4'],
    12: ['D6'],
    11: ['3 D4'],
    10: ['D5'],
    9: ['1 D4'],
    8: ['D4'],
    7: ['3 D2'],
    6: ['D3'],
    5: ['1 D2'],
    4: ['D2'],
    3: ['1 D1'],
    2: ['D1'],
};

Alpine.data('challengesHub', () => ({
    loading: true,
    metaLoading: true,
    leaderboardLoading: true,
    busyRemoveId: '',
    items: [],
    meta: { players: [], venues: [], summary: { pendingIncoming: 0, activeMine: 0, note: '' } },
    leaderboard: [],
    scope: 'mine',
    status: '',
    rule: '',
    webrtcDebugOpen: false,
    confirmDialogOpen: false,
    confirmDialogBusy: false,
    nowTick: Date.now(),
    _scheduleTickTimer: null,
    confirmDialog: {
        title: '',
        message: '',
        confirmLabel: 'Confirm',
        confirmTone: 'danger',
        challengeId: '',
        action: '',
        item: null,
    },

    async init() {
        await Promise.all([this.loadMeta(), this.loadItems(), this.loadLeaderboard()]);
        this._scheduleTickTimer = setInterval(() => {
            this.nowTick = Date.now();
        }, 30_000);
        this._onListRefresh = () => {
            this.loadItems();
            this.loadMeta();
        };
        document.addEventListener('challenge:list-refresh', this._onListRefresh);
        window.addEventListener('beforeunload', () => {
            if (this._scheduleTickTimer) {
                clearInterval(this._scheduleTickTimer);
                this._scheduleTickTimer = null;
            }
            if (this._onListRefresh) {
                document.removeEventListener('challenge:list-refresh', this._onListRefresh);
            }
        });
        this.loading = false;
    },

    async loadMeta() {
        this.metaLoading = true;
        try {
            const res = await apiFetch('/challenges/meta');
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to load challenge data');
            this.meta = data;
            const pr = await apiFetch('/players?limit=200&sortBy=fullName:asc&includeRemoved=false');
            const pj = await pr.json().catch(() => ({}));
            if (pr.ok && Array.isArray(pj.items)) {
                const byId = new Map();
                for (const p of [...(this.meta.players || []), ...pj.items]) {
                    const id = String(p?._id || p?.id || '');
                    if (!id) continue;
                    const seed = id || String(p.fullName || 'player');
                    byId.set(id, {
                        _id: id,
                        fullName: String(p.fullName || p.displayName || 'Player').trim() || 'Player',
                        nickname: String(p.nickname || '').trim(),
                        imageUrl: String(p.imageUrl || '').trim() || venuePersonaAvatarDataUri(seed),
                    });
                }
                this.meta.players = [...byId.values()];
            }
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to load challenge data');
        } finally {
            this.metaLoading = false;
        }
    },

    async loadItems() {
        this.loading = true;
        try {
            const qs = new URLSearchParams();
            qs.set('scope', this.scope);
            if (this.status) qs.set('status', this.status);
            const res = await apiFetch(`/challenges?${qs.toString()}`);
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to load challenges');
            this.items = data.items || [];
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to load challenges');
        } finally {
            this.loading = false;
        }
    },

    async loadLeaderboard() {
        this.leaderboardLoading = true;
        try {
            const res = await apiFetch('/challenges/leaderboard');
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to load ladder');
            this.leaderboard = data.items || [];
            this.rule = data.scoringRule || '';
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to load challenge ladder');
        } finally {
            this.leaderboardLoading = false;
        }
    },

    async applyFilters() {
        await this.loadItems();
    },

    currentPlayerId() {
        return rosterPlayerIdFromSession(this.$store.session, this.meta?.currentPlayerId);
    },

    opponentFor(item) {
        const uid = this.currentPlayerId();
        const challengerId = String(item?.challengerPlayerId?._id || item?.challengerPlayerId || '');
        return challengerId === uid ? item?.challengedPlayerId : item?.challengerPlayerId;
    },

    opponentName(item) {
        return playerName(this.opponentFor(item));
    },

    challengeTitle(item) {
        return item?.title || `${playerName(item?.challengerPlayerId)} vs ${playerName(item?.challengedPlayerId)}`;
    },

    statusClasses(status) {
        return statusMeta(status);
    },

    canRemoveListChallenge(item) {
        const me = String(this.$store.session?.me?._id || '');
        const ownerId = String(item?.createdByPlayerId || '');
        const isAdminRole = this.$store.session?.me?.role === 'admin';
        return (isAdminRole || ownerId === me) && ['pending', 'declined', 'cancelled'].includes(String(item?.status || ''));
    },

    canForceQuitListChallenge(item) {
        const me = String(this.$store.session?.me?._id || '');
        const ownerId = String(item?.createdByPlayerId || '');
        const isAdminRole = this.$store.session?.me?.role === 'admin';
        const status = String(item?.status || '');
        return (isAdminRole || ownerId === me) && ['accepted', 'in_progress', 'submitted', 'disputed'].includes(status);
    },

    openConfirmDialog(config = {}) {
        this.confirmDialog = {
            title: String(config.title || 'Confirm action'),
            message: String(config.message || ''),
            confirmLabel: String(config.confirmLabel || 'Confirm'),
            confirmTone: String(config.confirmTone || 'danger'),
            challengeId: String(config.challengeId || ''),
            action: String(config.action || ''),
            item: config.item || null,
        };
        this.confirmDialogOpen = true;
    },

    closeConfirmDialog() {
        if (this.confirmDialogBusy) return;
        this.confirmDialogOpen = false;
    },

    requestRemoveListChallenge(item) {
        if (!this.canRemoveListChallenge(item)) return;
        this.openConfirmDialog({
            action: 'remove',
            challengeId: String(item?._id || ''),
            item,
            title: 'Remove challenge?',
            message: 'This challenge and its session will be permanently deleted. This cannot be undone.',
            confirmLabel: 'Remove challenge',
            confirmTone: 'danger',
        });
    },

    requestForceQuitListChallenge(item) {
        if (!this.canForceQuitListChallenge(item)) return;
        this.openConfirmDialog({
            action: 'forceQuit',
            challengeId: String(item?._id || ''),
            item,
            title: 'Force quit this challenge?',
            message: 'This will immediately end any active session and remove the challenge from the list.',
            confirmLabel: 'Force quit challenge',
            confirmTone: 'warning',
        });
    },

    confirmButtonClasses() {
        return this.confirmDialog?.confirmTone === 'warning'
            ? 'bg-amber-500 text-neutral-900 hover:bg-amber-400'
            : 'bg-rose-600 text-white hover:bg-rose-500';
    },

    async runConfirmedAction() {
        if (this.confirmDialogBusy) return;
        const challengeId = String(this.confirmDialog?.challengeId || '');
        const action = String(this.confirmDialog?.action || '');
        const item = this.confirmDialog?.item || null;
        if (!challengeId || !action) return;
        this.confirmDialogBusy = true;
        try {
            if (action === 'remove') {
                await this.removeListChallenge(item || { _id: challengeId }, { skipConfirm: true });
            } else if (action === 'forceQuit') {
                await this.forceQuitListChallenge(item || { _id: challengeId }, { skipConfirm: true });
            }
            this.confirmDialogOpen = false;
        } finally {
            this.confirmDialogBusy = false;
        }
    },

    async removeListChallenge(item, options = {}) {
        if (!this.canRemoveListChallenge(item)) return;
        if (!options.skipConfirm) {
            this.requestRemoveListChallenge(item);
            return;
        }
        this.busyRemoveId = String(item?._id || '');
        try {
            const res = await apiFetch(`/challenges/${item._id}/remove`, { method: 'POST', body: {} });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to remove challenge');
            this.items = this.items.filter((entry) => String(entry?._id || '') !== String(item?._id || ''));
            this.$store.toastr.success('Challenge removed', 3000);
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to remove challenge', 3000);
        } finally {
            this.busyRemoveId = '';
        }
    },

    async forceQuitListChallenge(item, options = {}) {
        if (!this.canForceQuitListChallenge(item)) return;
        if (!options.skipConfirm) {
            this.requestForceQuitListChallenge(item);
            return;
        }
        this.busyRemoveId = String(item?._id || '');
        try {
            const res = await apiFetch(`/challenges/${item._id}/remove`, { method: 'POST', body: { force: true } });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to force quit challenge');
            this.items = this.items.filter((entry) => String(entry?._id || '') !== String(item?._id || ''));
            this.$store.toastr.success('Challenge force-quit and removed', 3000);
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to force quit challenge', 3000);
        } finally {
            this.busyRemoveId = '';
        }
    },

    modeLabel(mode) {
        return modeMeta(mode).label;
    },

    formatDate(value) {
        return fmtDateTime(value);
    },

    playerLabel(player) {
        return playerName(player);
    },

    emptyStateTitle() {
        if (this.scope === 'incoming') return 'No incoming challenges';
        if (this.scope === 'outgoing') return 'No outgoing challenges';
        return 'No challenges yet';
    },

    async startChallengeForPlayer(playerId) {
        const opponentId = String(playerId || '').trim();
        if (!opponentId) return;
        this.busyRemoveId = opponentId;
        try {
            const res = await apiFetch('/challenges', {
                method: 'POST',
                body: {
                    opponentType: 'player',
                    challengedPlayerId: opponentId,
                    mode: 'local',
                    scheduledFor: null,
                    venueId: null,
                    format: {
                        structure: 'legs',
                        bestOfLegs: 3,
                        bestOfSets: 3,
                        legsPerSet: 3,
                        startingScore: 501,
                        checkout: 'double-out',
                    },
                },
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Unable to create challenge');
            const opp = this.meta.players?.find((p) => String(p._id) === opponentId);
            const oppName = opp ? this.playerLabel(opp) : 'Player';
            this.$store.toastr.success(`Challenge sent to ${oppName}`, 3500);
            await this.loadItems();
            await this.loadMeta();
            this.$store.challengeNotifications?.refresh?.();
            if (String(data?.challenge?.status || '') === 'accepted') {
                window.location.assign(`/admin/challenges/${data.challengeId}/scorer`);
            }
        } catch (e) {
            this.$store.toastr.error(e?.message || 'Unable to create challenge', 3000);
        } finally {
            this.busyRemoveId = '';
        }
    },

    isScheduledChallenge(item) {
        const ts = item?.scheduledFor ? new Date(item.scheduledFor).getTime() : 0;
        return Number.isFinite(ts) && ts > this.nowTick;
    },

    scheduleCountdownLabel(item) {
        const ts = item?.scheduledFor ? new Date(item.scheduledFor).getTime() : 0;
        if (!Number.isFinite(ts) || ts <= 0) return '';
        const deltaMs = ts - this.nowTick;
        if (deltaMs <= 0) return 'Starting now';
        const mins = Math.round(deltaMs / 60000);
        if (mins < 60) return `Starts in ${mins}m`;
        const hrs = Math.floor(mins / 60);
        const remMins = mins % 60;
        if (hrs < 24) return `Starts in ${hrs}h ${remMins}m`;
        const days = Math.floor(hrs / 24);
        const remHrs = hrs % 24;
        return `Starts in ${days}d ${remHrs}h`;
    },

    scheduleDateLine(item) {
        const when = item?.scheduledFor || item?.createdAt;
        if (!when) return '';
        const prefix = item?.scheduledFor ? 'Game start' : 'Created';
        return `${prefix}: ${fmtDateTime(when)}`;
    },

    canStartListChallenge(item) {
        const status = String(item?.status || '');
        if (!['accepted', 'submitted', 'disputed'].includes(status)) return false;
        const uid = this.currentPlayerId();
        const ch = String(item?.challengerPlayerId?._id || item?.challengerPlayerId || '');
        const op = String(item?.challengedPlayerId?._id || item?.challengedPlayerId || '');
        return this.$store.session?.me?.role === 'admin' || uid === ch || uid === op;
    },

    async startListChallenge(item) {
        if (!this.canStartListChallenge(item)) return;
        const id = String(item?._id || '');
        if (!id) return;
        this.busyRemoveId = id;
        try {
            const res = await apiFetch(`/challenges/${id}/start`, { method: 'POST', body: {} });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Unable to start challenge');
            const nextChallenge = data?.challenge || null;
            this.items = this.items.map((entry) => (
                String(entry?._id || '') === id
                    ? { ...entry, ...(nextChallenge || {}), status: String(nextChallenge?.status || 'in_progress') }
                    : entry
            ));
            this.$store.toastr.success('Challenge started', 2500);
            window.location.href = `/admin/challenges/${id}/score`;
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to start challenge', 3000);
        } finally {
            this.busyRemoveId = '';
        }
    },
}));

Alpine.data('challengeCreate', () => ({
    loading: true,
    saving: false,
    playersLoading: false,
    meta: { players: [], venues: [] },
    mePlayer: null,
    directoryResults: [],
    directoryCarouselIndex: 0,
    directoryCarouselAnimating: false,
    carouselTouchStartX: null,
    directorySearching: false,
    directoryFilterLevel: '',
    directoryMinAvg: 0,
    directoryLevelOptions: ['beginner', 'intermediate', 'advanced', 'pro'],
    directorySelectedOpponent: null,
    guestAvatarChoices: [],
    guestAvatarCarouselIndex: 0,
    guestAvatarCarouselAnimating: false,
    guestCarouselTouchStartX: null,
    playerQuery: '',
    defaultPlayerPreviewCount: 10,
    venueQuery: '',
    playerAutocompleteOpen: false,
    venueAutocompleteOpen: false,
    checkoutOptions: [
        { value: 'double-out', label: 'DOUBLE', shortLabel: 'DOUBLE' },
        { value: 'straight-out', label: 'STRAIGHT', shortLabel: 'STRAIGHT' },
        { value: 'master-out', label: 'MASTER', shortLabel: 'MASTER' },
    ],
    startInOptions: [
        { value: 'straight-in', label: 'STRAIGHT', shortLabel: 'STRAIGHT' },
        { value: 'double-in', label: 'DOUBLE', shortLabel: 'DOUBLE' },
        { value: 'master-in', label: 'MASTER', shortLabel: 'MASTER' },
    ],
    bestOfLegsOptions: [1, 3, 5, 7, 9, 11, 13, 15],
    bestOfSetsOptions: [1, 3, 5, 7, 9, 11, 13, 15],
    legsPerSetOptions: [1, 3, 5, 7, 9, 11, 13, 15],
    form: {
        opponentType: 'player',
        challengedPlayerId: '',
        challengedGuestName: '',
        challengedGuestAvatarSeed: '',
        mode: 'local',
        notes: '',
        scheduledFor: '',
        venueId: '',
        structure: 'legs',
        bestOfLegs: 3,
        bestOfSets: 3,
        legsPerSet: 3,
        startingScore: 501,
        startIn: 'straight-in',
        checkout: 'double-out',
        streamProtocol: 'rtsp',
        streamPrimaryUrl: '',
        streamSecondaryUrl: '',
        streamLabel: '',
        streamNotes: '',
        advancedStreaming: false,
    },

    playerName,

    readPreferredOpponentId() {
        const queryOpponentId = String(challengeQueryParam('opponent') || '').trim();
        if (queryOpponentId) return queryOpponentId;
        try {
            return String(sessionStorage.getItem('challenge:new:opponent') || '').trim();
        } catch {
            return '';
        }
    },

    clearPreferredOpponentId() {
        try {
            sessionStorage.removeItem('challenge:new:opponent');
        } catch { }
    },

    normalizeDirectoryPlayer(p) {
        if (!p) return null;
        const id = String(p._id || p.id || '').trim();
        if (!id) return null;
        const stats = p.stats && typeof p.stats === 'object' ? p.stats : {};
        return {
            _id: id,
            fullName: String(p.fullName || p.displayName || 'Player').trim() || 'Player',
            nickname: String(p.nickname || '').trim(),
            imageUrl: String(p.imageUrl || '').trim(),
            skillLevel: p.skillLevel || stats.skillLevel || null,
            threeDartAvg: p.threeDartAvg ?? stats.threeDartAvg ?? null,
            stats,
        };
    },

    async loadMePlayer() {
        try {
            const res = await apiFetch('/auth/me');
            const data = await res.json().catch(() => ({}));
            if (!res.ok) return;
            const p = data?.player || {};
            const id = String(p.playerId || p._id || p.id || '').trim();
            this.mePlayer = this.normalizeDirectoryPlayer({
                _id: id,
                fullName: p.fullName || p.displayName || data.fullName,
                nickname: p.nickname,
                imageUrl: p.imageUrl,
                skillLevel: p.skillLevel,
                threeDartAvg: p.threeDartAvg,
                stats: p.stats,
            });
            if (this.mePlayer?._id && !this.mePlayer.stats) {
                const card = await apiFetch(`/players/directory/${encodeURIComponent(this.mePlayer._id)}`);
                const cardJson = await card.json().catch(() => ({}));
                if (card.ok && cardJson?.player) {
                    this.mePlayer = this.normalizeDirectoryPlayer(cardJson.player);
                }
            }
        } catch {
            this.mePlayer = null;
        }
    },

    async searchDirectoryPlayers({ suggest = false } = {}) {
        this.directorySearching = true;
        try {
            const params = new URLSearchParams();
            const q = String(this.playerQuery || '').trim();
            if (q) params.set('q', q);
            params.set('limit', suggest ? '12' : '20');
            if (this.directoryFilterLevel) params.set('level', this.directoryFilterLevel);
            if (this.directoryMinAvg > 0) params.set('minAvg', String(this.directoryMinAvg));
            if (suggest) params.set('suggest', '1');
            const res = await apiFetch(`/players/directory?${params.toString()}`);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data?.message || data?.error || `Player search failed (${res.status})`);
            }
            this.directoryResults = (Array.isArray(data.items) ? data.items : [])
                .map((row) => this.normalizeDirectoryPlayer(row))
                .filter(Boolean);
            this.syncCarouselIndexToSelection();
            const byId = new Map((this.meta.players || []).map((p) => [String(p._id), p]));
            for (const p of this.directoryResults) byId.set(String(p._id), p);
            this.meta.players = [...byId.values()].sort((a, b) =>
                String(a.fullName || '').localeCompare(String(b.fullName || '')),
            );
        } catch (e) {
            this.directoryResults = [];
            this.$store?.toastr?.error?.(e?.message || 'Could not search players');
        } finally {
            this.directorySearching = false;
            this.playersLoading = false;
        }
    },

    openDirectorySearch() {
        if (!this.directoryResults.length) {
            this.searchDirectoryPlayers({ suggest: true });
        }
    },

    onDirectoryQueryInput() {
        this.clearOpponentSelection();
        this.searchDirectoryPlayers({ suggest: !String(this.playerQuery || '').trim() });
    },

    setDirectoryLevel(level) {
        this.directoryFilterLevel = String(level || '').trim().toLowerCase();
        this.searchDirectoryPlayers({ suggest: !String(this.playerQuery || '').trim() });
    },

    setDirectoryMinAvg(value) {
        this.directoryMinAvg = Number(value) || 0;
        this.searchDirectoryPlayers({ suggest: !String(this.playerQuery || '').trim() });
    },

    duelAvatarUrl(player) {
        const url = String(player?.imageUrl || '').trim();
        if (url) return url;
        const seed = encodeURIComponent(String(player?._id || player?.fullName || 'player'));
        return `https://api.dicebear.com/7.x/personas/svg?seed=${seed}`;
    },

    onDuelAvatarError(event) {
        const img = event?.target;
        if (!img || img.dataset.fallback) return;
        img.dataset.fallback = '1';
        img.src = '/assets/placeholder-player.png';
    },

    skillLevelLabel(level) {
        const s = String(level || '').trim().toLowerCase();
        if (s === 'beginner') return 'Beginner';
        if (s === 'intermediate') return 'Intermediate';
        if (s === 'advanced') return 'Advanced';
        if (s === 'pro') return 'Pro';
        return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
    },

    skillLevelClass(level) {
        const s = String(level || 'intermediate').trim().toLowerCase();
        return `challenge-duel-skill challenge-duel-skill--${s}`;
    },

    currentCarouselPlayer() {
        if (!this.directoryResults.length) return null;
        const idx = Math.min(
            Math.max(0, this.directoryCarouselIndex),
            this.directoryResults.length - 1,
        );
        return this.directoryResults[idx] || null;
    },

    syncCarouselIndexToSelection() {
        if (!this.directoryResults.length) {
            this.directoryCarouselIndex = 0;
            return;
        }
        const selectedId = String(this.form.challengedPlayerId || '').trim();
        if (selectedId) {
            const idx = this.directoryResults.findIndex((p) => String(p._id) === selectedId);
            if (idx >= 0) {
                this.directoryCarouselIndex = idx;
                return;
            }
        }
        if (this.directoryCarouselIndex >= this.directoryResults.length) {
            this.directoryCarouselIndex = 0;
        }
    },

    carouselTrackStyle() {
        const pct = this.directoryCarouselIndex * 100;
        return `transform: translate3d(-${pct}%, 0, 0)`;
    },

    carouselCanPrev() {
        return this.directoryCarouselIndex > 0 && !this.directoryCarouselAnimating;
    },

    carouselCanNext() {
        return this.directoryResults.length > 0
            && this.directoryCarouselIndex < this.directoryResults.length - 1
            && !this.directoryCarouselAnimating;
    },

    carouselStep(delta) {
        const step = Number(delta) || 0;
        if (!step || !this.directoryResults.length || this.directoryCarouselAnimating) return;
        const next = this.directoryCarouselIndex + step;
        if (next < 0 || next >= this.directoryResults.length) return;
        this.directoryCarouselAnimating = true;
        this.directoryCarouselIndex = next;
        window.setTimeout(() => {
            this.directoryCarouselAnimating = false;
        }, 320);
    },

    goToCarouselIndex(idx) {
        const target = Number(idx);
        if (!Number.isFinite(target) || !this.directoryResults.length || this.directoryCarouselAnimating) return;
        if (target < 0 || target >= this.directoryResults.length) return;
        if (target === this.directoryCarouselIndex) return;
        this.directoryCarouselAnimating = true;
        this.directoryCarouselIndex = target;
        window.setTimeout(() => {
            this.directoryCarouselAnimating = false;
        }, 320);
    },

    carouselTouchStart(event) {
        const touch = event?.changedTouches?.[0] || event?.touches?.[0];
        this.carouselTouchStartX = touch ? touch.clientX : null;
    },

    carouselTouchEnd(event) {
        const startX = this.carouselTouchStartX;
        this.carouselTouchStartX = null;
        if (startX == null) return;
        const touch = event?.changedTouches?.[0];
        if (!touch) return;
        const deltaX = touch.clientX - startX;
        const threshold = 48;
        if (Math.abs(deltaX) < threshold) return;
        this.carouselStep(deltaX < 0 ? 1 : -1);
    },

    isCarouselPlayerSelected() {
        const current = this.currentCarouselPlayer();
        return Boolean(
            current?._id
            && String(this.form.challengedPlayerId) === String(current._id),
        );
    },

    confirmCarouselOpponent() {
        const player = this.currentCarouselPlayer();
        if (player) this.chooseDirectoryOpponent(player);
    },

    initGuestDuel() {
        if (!String(this.form.challengedGuestAvatarSeed || '').trim()) {
            this.form.challengedGuestAvatarSeed = newGuestAvatarSeed();
        }
        this.rebuildGuestAvatarChoices(this.form.challengedGuestAvatarSeed);
    },

    rebuildGuestAvatarChoices(seed) {
        const cur = String(seed || this.form.challengedGuestAvatarSeed || newGuestAvatarSeed());
        this.guestAvatarChoices = buildGuestAvatarChoices(cur);
        const idx = this.guestAvatarChoices.findIndex((c) => c.seed === cur);
        this.guestAvatarCarouselIndex = idx >= 0 ? idx : 0;
        this.syncGuestAvatarFromCarousel();
    },

    syncGuestAvatarFromCarousel() {
        const choice = this.guestAvatarChoices[this.guestAvatarCarouselIndex];
        if (choice?.seed) this.form.challengedGuestAvatarSeed = choice.seed;
    },

    guestDuelAvatarUrl() {
        const seed = String(this.form.challengedGuestAvatarSeed || 'guest-opponent').trim() || 'guest-opponent';
        return venuePersonaAvatarDataUri(seed);
    },

    guestDisplayName() {
        return String(this.form.challengedGuestName || '').trim() || 'Guest opponent';
    },

    guestOpponentReady() {
        return !!String(this.form.challengedGuestName || '').trim();
    },

    generateGuestName() {
        const exclude = String(this.mePlayer?.fullName || '').trim();
        this.form.challengedGuestName = pickRandomGuestName(exclude);
    },

    shuffleGuestAvatars() {
        const nextSeed = newGuestAvatarSeed();
        this.rebuildGuestAvatarChoices(nextSeed);
    },

    guestCarouselTrackStyle() {
        const pct = this.guestAvatarCarouselIndex * 100;
        return `transform: translate3d(-${pct}%, 0, 0)`;
    },

    guestCarouselCanPrev() {
        return this.guestAvatarCarouselIndex > 0 && !this.guestAvatarCarouselAnimating;
    },

    guestCarouselCanNext() {
        return this.guestAvatarChoices.length > 0
            && this.guestAvatarCarouselIndex < this.guestAvatarChoices.length - 1
            && !this.guestAvatarCarouselAnimating;
    },

    guestCarouselStep(delta) {
        const step = Number(delta) || 0;
        if (!step || !this.guestAvatarChoices.length || this.guestAvatarCarouselAnimating) return;
        const next = this.guestAvatarCarouselIndex + step;
        if (next < 0 || next >= this.guestAvatarChoices.length) return;
        this.guestAvatarCarouselAnimating = true;
        this.guestAvatarCarouselIndex = next;
        this.syncGuestAvatarFromCarousel();
        window.setTimeout(() => {
            this.guestAvatarCarouselAnimating = false;
        }, 320);
    },

    goToGuestCarouselIndex(idx) {
        const target = Number(idx);
        if (!Number.isFinite(target) || !this.guestAvatarChoices.length || this.guestAvatarCarouselAnimating) return;
        if (target < 0 || target >= this.guestAvatarChoices.length) return;
        if (target === this.guestAvatarCarouselIndex) return;
        this.guestAvatarCarouselAnimating = true;
        this.guestAvatarCarouselIndex = target;
        this.syncGuestAvatarFromCarousel();
        window.setTimeout(() => {
            this.guestAvatarCarouselAnimating = false;
        }, 320);
    },

    guestCarouselTouchStart(event) {
        const touch = event?.changedTouches?.[0] || event?.touches?.[0];
        this.guestCarouselTouchStartX = touch ? touch.clientX : null;
    },

    guestCarouselTouchEnd(event) {
        const startX = this.guestCarouselTouchStartX;
        this.guestCarouselTouchStartX = null;
        if (startX == null) return;
        const touch = event?.changedTouches?.[0];
        if (!touch) return;
        const deltaX = touch.clientX - startX;
        const threshold = 48;
        if (Math.abs(deltaX) < threshold) return;
        this.guestCarouselStep(deltaX < 0 ? 1 : -1);
    },

    chooseDirectoryOpponent(player) {
        const row = this.normalizeDirectoryPlayer(player);
        if (!row?._id) return;
        this.directorySelectedOpponent = row;
        this.form.challengedPlayerId = row._id;
        const idx = this.directoryResults.findIndex((p) => String(p._id) === String(row._id));
        if (idx >= 0) this.directoryCarouselIndex = idx;
        const byId = new Map((this.meta.players || []).map((p) => [String(p._id), p]));
        byId.set(String(row._id), row);
        this.meta.players = [...byId.values()];
    },

    async init() {
        try {
            const res = await apiFetch('/challenges/meta');
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to load challenge setup');
            this.meta = data;
            this.playersLoading = true;
            await Promise.all([
                this.loadMePlayer(),
                this.searchDirectoryPlayers({ suggest: true }),
            ]);

            const preferredOpponentId = this.readPreferredOpponentId();
            let preferred = preferredOpponentId
                ? this.directoryResults.find((p) => String(p._id) === preferredOpponentId)
                : null;
            if (!preferred && preferredOpponentId) {
                try {
                    const pr = await apiFetch(`/players/directory/${encodeURIComponent(preferredOpponentId)}`);
                    const pj = await pr.json().catch(() => ({}));
                    if (pr.ok && pj?.player) preferred = this.normalizeDirectoryPlayer(pj.player);
                } catch { /* ignore */ }
            }

            if (preferred?._id) {
                this.form.opponentType = 'player';
                this.chooseDirectoryOpponent(preferred);
            } else if (data.players?.length === 1) {
                this.chooseDirectoryOpponent(this.normalizeDirectoryPlayer(data.players[0]));
            }
            this.clearPreferredOpponentId();
            this.syncVenueQueryFromSelected();
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to load challenge setup');
        } finally {
            this.loading = false;
        }

        await this.$nextTick();
        const waitRefs = (pred, tries = 50) => new Promise((res) => {
            const t = setInterval(() => {
                if (pred() || --tries <= 0) {
                    clearInterval(t);
                    res();
                }
            }, 40);
        });
        await waitRefs(() => this.$refs.scheduledForPicker);
        mountAdminDatetime(this.$refs.scheduledForPicker, {
            defaultDate: parseToDate(this.form.scheduledFor) || undefined,
            onChange: (selected) => {
                const dt = selected?.[0];
                this.form.scheduledFor = dt ? dateToDatetimeLocalString(dt) : '';
            },
        });
    },

    sanitizeBestOfLegs(raw) {
        const values = this.bestOfLegsOptions;
        const fallback = 3;
        const n = Number(raw);
        if (!Number.isFinite(n)) return fallback;
        if (values.includes(n)) return n;
        return values.reduce((best, cur) => (Math.abs(cur - n) < Math.abs(best - n) ? cur : best), fallback);
    },

    sanitizeOddOption(raw, values, fallback = 3) {
        const n = Number(raw);
        if (!Number.isFinite(n)) return fallback;
        if (values.includes(n)) return n;
        return values.reduce((best, cur) => (Math.abs(cur - n) < Math.abs(best - n) ? cur : best), fallback);
    },

    setBestOfLegs(value) {
        this.form.bestOfLegs = this.sanitizeBestOfLegs(value);
    },

    bumpBestOfLegs(delta) {
        const values = this.bestOfLegsOptions;
        const current = this.sanitizeBestOfLegs(this.form.bestOfLegs);
        const idx = Math.max(0, values.indexOf(current));
        const next = Math.max(0, Math.min(values.length - 1, idx + (delta > 0 ? 1 : -1)));
        this.form.bestOfLegs = values[next];
    },

    setStructure(value) {
        this.form.structure = String(value || '') === 'sets' ? 'sets' : 'legs';
    },

    setBestOfSets(value) {
        this.form.bestOfSets = this.sanitizeOddOption(value, this.bestOfSetsOptions, 3);
    },

    setLegsPerSet(value) {
        this.form.legsPerSet = this.sanitizeOddOption(value, this.legsPerSetOptions, 3);
    },

    hasValidOpponent() {
        if (this.isGuestOpponent()) return !!String(this.form.challengedGuestName || '').trim();
        return !!String(this.form.challengedPlayerId || '').trim();
    },

    hasValidVenueSelection() {
        return true;
    },

    canSubmitCreate() {
        return this.hasValidOpponent()
            && this.hasValidVenueSelection()
            && Number(this.form.startingScore || 0) >= 101;
    },

    checkoutLabel(value) {
        const found = this.checkoutOptions.find((opt) => opt.value === String(value || ''));
        return found?.shortLabel || checkoutRuleShortLabel(value);
    },

    startInLabel(value) {
        const found = this.startInOptions.find((opt) => opt.value === String(value || ''));
        return found?.shortLabel || startInRuleShortLabel(value);
    },

    selectedOpponentPlayer() {
        if (this.directorySelectedOpponent
            && String(this.directorySelectedOpponent._id) === String(this.form.challengedPlayerId)) {
            return this.directorySelectedOpponent;
        }
        return this.meta.players.find((player) => String(player._id) === String(this.form.challengedPlayerId)) || null;
    },

    selectedVenue() {
        return this.meta.venues.find((venue) => String(venue._id) === String(this.form.venueId)) || null;
    },

    syncPlayerQueryFromSelected() {
        const player = this.selectedOpponentPlayer();
        this.playerQuery = player ? playerName(player) : '';
    },

    syncVenueQueryFromSelected() {
        const venue = this.selectedVenue();
        this.venueQuery = venue?.name || '';
    },

    filteredPlayers() {
        const q = String(this.playerQuery || '').trim().toLowerCase();
        const list = Array.isArray(this.meta.players) ? this.meta.players : [];
        const cap = this.defaultPlayerPreviewCount;
        if (!q) return list.slice(0, cap);
        return list
            .filter((player) => {
                const full = String(player?.fullName || '').toLowerCase();
                const nick = String(player?.nickname || '').toLowerCase();
                const id = String(player?._id || '').toLowerCase();
                return full.includes(q) || nick.includes(q) || id.includes(q);
            })
            .slice(0, 20);
    },

    hasLoadedPlayers() {
        return Array.isArray(this.meta.players) && this.meta.players.length > 0;
    },

    filteredVenues() {
        const q = String(this.venueQuery || '').trim().toLowerCase();
        const list = Array.isArray(this.meta.venues) ? this.meta.venues : [];
        if (!q) return list.slice(0, 12);
        return list
            .filter((venue) => String(venue?.name || '').toLowerCase().includes(q))
            .slice(0, 12);
    },

    chooseOpponent(player) {
        this.chooseDirectoryOpponent(player);
        this.playerAutocompleteOpen = false;
    },

    chooseVenue(venue) {
        if (!venue?._id) return;
        this.form.venueId = String(venue._id);
        this.venueQuery = String(venue.name || '');
        this.venueAutocompleteOpen = false;
    },

    clearOpponentSelection() {
        this.form.challengedPlayerId = '';
        this.directorySelectedOpponent = null;
    },

    clearVenueSelection() {
        this.form.venueId = '';
    },

    resolveOpponentFromQuery() {
        if (this.form.challengedPlayerId) return true;
        const q = String(this.playerQuery || '').trim().toLowerCase();
        if (!q) return false;
        const pools = [
            ...(this.directoryResults || []),
            ...(this.meta.players || []),
        ];
        const match = pools.find((player) => {
            const full = String(player?.fullName || '').trim().toLowerCase();
            const nick = String(player?.nickname || '').trim().toLowerCase();
            return q === full || (nick && q === nick) || q === playerName(player).toLowerCase();
        });
        if (!match?._id) return false;
        this.chooseDirectoryOpponent(match);
        return true;
    },

    resolveVenueFromQuery() {
        if (this.form.venueId) return true;
        const q = String(this.venueQuery || '').trim().toLowerCase();
        if (!q) return false;
        const match = (this.meta.venues || []).find((venue) => q === String(venue?.name || '').trim().toLowerCase());
        if (!match?._id) return false;
        this.chooseVenue(match);
        return true;
    },

    scheduledPreview() {
        if (!this.form.scheduledFor) return 'Choose a date and time';
        return fmtDateTime(this.form.scheduledFor);
    },

    setGameStartNow() {
        this.form.scheduledFor = dateToDatetimeLocalString(new Date());
        const picker = this.$refs?.scheduledForPicker?._flatpickr;
        try {
            picker?.setDate?.(new Date(), true);
        } catch { }
    },

    isGuestOpponent() {
        return String(this.form.opponentType || '') === 'guest';
    },

    setOpponentType(type) {
        this.form.opponentType = type === 'guest' ? 'guest' : 'player';
        if (this.isGuestOpponent()) {
            this.form.challengedPlayerId = '';
            this.form.mode = 'local';
            this.playerQuery = '';
            this.playerAutocompleteOpen = false;
            this.directorySelectedOpponent = null;
            this.initGuestDuel();
        } else {
            this.form.challengedGuestName = '';
            this.form.challengedGuestAvatarSeed = '';
            this.guestAvatarChoices = [];
        }
    },

    setMode(mode) {
        if (this.isGuestOpponent()) {
            this.form.mode = 'local';
            return;
        }
        this.form.mode = mode === 'remote' ? 'remote' : 'local';
        if (this.form.mode === 'remote') {
            this.form.streamProtocol = 'webrtc';
            if (!this.form.streamLabel) this.form.streamLabel = 'Quick Camera Session';
        } else {
            this.form.advancedStreaming = false;
        }
    },

    async submit() {
        if (this.isGuestOpponent()) {
            if (!String(this.form.challengedGuestName || '').trim()) {
                this.$store.toastr.error('Enter a guest name', 3000);
                return;
            }
        } else {
            this.resolveOpponentFromQuery();
            if (!this.form.challengedPlayerId) {
                this.$store.toastr.error('Select an existing player opponent', 3000);
                return;
            }
        }
        if (this.form.mode === 'local' || this.isGuestOpponent()) {
            this.resolveVenueFromQuery();
        }
        this.form.bestOfLegs = this.sanitizeBestOfLegs(this.form.bestOfLegs);
        this.form.bestOfSets = this.sanitizeOddOption(this.form.bestOfSets, this.bestOfSetsOptions, 3);
        this.form.legsPerSet = this.sanitizeOddOption(this.form.legsPerSet, this.legsPerSetOptions, 3);
        if (!this.canSubmitCreate()) {
            this.$store.toastr.error('Complete all required challenge details', 3000);
            return;
        }

        this.saving = true;
        try {
            const payload = {
                opponentType: this.form.opponentType,
                challengedPlayerId: this.isGuestOpponent() ? null : this.form.challengedPlayerId,
                challengedGuestName: this.isGuestOpponent() ? this.form.challengedGuestName : '',
                challengedGuestAvatarSeed: this.isGuestOpponent()
                    ? (this.form.challengedGuestAvatarSeed || null)
                    : null,
                mode: this.isGuestOpponent() ? 'local' : this.form.mode,
                notes: this.form.notes,
                scheduledFor: this.form.scheduledFor || null,
                venueId: (this.isGuestOpponent() || this.form.mode === 'local') ? (this.form.venueId || null) : null,
                format: {
                    structure: this.form.structure === 'sets' ? 'sets' : 'legs',
                    bestOfLegs: this.form.bestOfLegs,
                    bestOfSets: this.form.bestOfSets,
                    legsPerSet: this.form.legsPerSet,
                    startingScore: this.form.startingScore,
                    startIn: this.form.startIn,
                    checkout: this.form.checkout,
                },
                stream: !this.isGuestOpponent() && this.form.mode === 'remote' ? {
                    protocol: this.form.advancedStreaming ? this.form.streamProtocol : 'webrtc',
                    primaryUrl: this.form.advancedStreaming ? this.form.streamPrimaryUrl : '',
                    secondaryUrl: this.form.advancedStreaming ? this.form.streamSecondaryUrl : '',
                    label: this.form.advancedStreaming ? this.form.streamLabel : 'Quick Camera Session',
                    notes: this.form.advancedStreaming ? this.form.streamNotes : 'Plug-and-play browser camera connection',
                } : null,
            };

            const res = await apiFetch('/challenges', { method: 'POST', body: payload });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to create challenge');
            const status = String(data?.challenge?.status || '');
            if (status === 'pending') {
                this.$store.toastr.success('Challenge sent — waiting for a response', 3500);
                this.$store.challengeNotifications?.refresh?.();
                window.location.href = '/admin/challenges';
                return;
            }
            this.$store.toastr.success('Challenge created', 3000);
            const target = (this.form.mode === 'local' || this.isGuestOpponent()) ? 'scorer' : 'score';
            window.location.href = `/admin/challenges/${data.challengeId}/${target}`;
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to create challenge', 3000);
        } finally {
            this.saving = false;
        }
    },
}));

Alpine.data('challengeView', () => ({
    loading: true,
    busy: false,
    challenge: null,
    /** 'console' | 'matchConsole' | 'scorer' — from URL path */
    surface: challengeSurfaceFromPath(),
    scoringWindow: false,
    scorerQrCanvas: null,
    session: null,
    sessionLoading: false,
    mediaBusy: false,
    localStream: null,
    remoteStream: null,
    micMuted: false,
    localMicLevel: 0,
    audioContext: null,
    audioAnalyser: null,
    audioMeterFrame: null,
    audioSourceNode: null,
    remoteMicLevel: 0,
    remoteAudioContext: null,
    remoteAudioAnalyser: null,
    remoteAudioMeterFrame: null,
    remoteAudioSourceNode: null,
    peer: null,
    webrtcReady: false,
    signalingTimer: null,
    heartbeatTimer: null,
    challengePollTimer: null,
    eventSource: null,
    challengeEventSource: null,
    eventStreamHealthy: false,
    challengeStreamHealthy: false,
    liveFocusMode: false,
    opponentFocusDismissed: false,
    sessionSetupOpen: false,
    lastSessionStatus: '',
    mediaStatusText: 'Awaiting camera connection',
    diagnosticsBusy: false,
    seenOfferCandidates: new Set(),
    seenAnswerCandidates: new Set(),
    remoteDescriptionApplied: false,
    answerSent: false,
    appliedOfferSdp: '',
    appliedAnswerSdp: '',
    iceRestartAttempts: 0,
    iceRestartInFlight: false,
    peerFailureToastTimer: null,
    signalingGeneration: -1,
    _localCameraAutoStartAttempted: false,
    _localCameraPermissionDenied: false,
    _lobbyCameraAutoStartTimer: null,
    kioskCameraSource: readSavedKioskCameraSource() || 'venue-pc',
    cameraSourceSwitchBusy: false,
    venueCameraHostStatus: null,
    fallbackForm: {
        enabled: false,
        sourceType: 'rtsp',
        sourceUrl: '',
        relayUrl: '',
        publicViewerUrl: '',
        label: '',
        notes: '',
    },
    scoreRows: [],
    disputeOpen: false,
    disputeReason: '',
    interruptionDialogOpen: false,
    interruptionDialog: {
        key: '',
        title: '',
        message: '',
        allowResume: false,
        allowDispute: false,
        forceExit: false,
    },
    suppressNextSessionEndedDialog: false,
    liveVisitForm: {
        score: '',
        checkoutDarts: '',
        checkoutLabel: '',
    },
    liveEditingVisitIndex: -1,
    liveEditingVisitPreScore: null,
    quickScoresExpanded: false,
    quickScoreTop: [],
    quickScoreMore: [],
    tvKioskPairCode: '',
    tvKioskPairBusy: false,
    tvKioskPairMessage: '',
    tvKioskPairError: '',
    broadcastSession: null,
    broadcastBusy: false,
    broadcastMessage: '',
    broadcastError: '',
    broadcastWatchUrl: '',
    broadcastYoutubeActive: false,
    broadcastYoutubeConfigured: false,
    goLivePinDialogOpen: false,
    goLivePinInput: '',
    goLivePinError: '',
    _goLivePinPendingAction: '',
    tvKioskLinkedActive: false,
    /** Remote: user opened the board-camera column while waiting to reconnect */
    liveVideoPanelExpanded: false,
    /** Remote: user hid the column while a stream is active (focus scorer) */
    liveVideoPanelSuppressed: false,
    /** Suppress repeat toasts when reload() polls the same pending challenge */
    _pendingChallengeNoticeToastId: '',
    embedMode: false,
    fromVenueKiosk: false,
    _vkShellMessageListener: null,

    async init() {
        let embed = false;
        try {
            const u = new URL(window.location.href);
            embed = u.searchParams.get('embed') === '1' || location.pathname.includes('/challenge-score/') || location.pathname.includes('/challenge-lobby/');
            this.fromVenueKiosk = u.searchParams.get('from') === 'venue-kiosk' || location.pathname.includes('/venue-kiosk/');
        } catch {
            embed = location.pathname.includes('/challenge-score/') || location.pathname.includes('/challenge-lobby/');
            this.fromVenueKiosk = false;
        }
        this.embedMode = embed;
        if (this.embedMode && this.fromVenueKiosk) {
            this._vkShellMessageListener = (ev) => {
                try {
                    if (!ev?.data || typeof ev.data !== 'object') return;
                    const origin = String(globalThis.location?.origin || '');
                    if (origin && String(ev.origin || '') !== origin) return;
                    if (ev.data?.type === 'challenge-scorer-leave') {
                        this.postVenueKioskShellMessage({ type: 'challenge-scorer-leave' });
                    }
                } catch { /* ignore */ }
            };
            globalThis.window?.addEventListener('message', this._vkShellMessageListener);
        }
        if (!this._challengeViewChromeUnload) {
            this._challengeViewChromeUnload = true;
            window.addEventListener('beforeunload', () => applyDedicatedChromeMode(false));
        }
        const q0 = loadScorerQuickConfig(CHALLENGE_SCORER_QUICK_LS);
        this.quickScoreTop = q0.top;
        this.quickScoreMore = q0.more;
        await this.reload();
        if (this.isVenueKioskEmbed()) {
            void this.refreshVenueCameraHostStatus();
        }
    },

    isVenueKioskEmbed() {
        return !!this.fromVenueKiosk && this.challengeKioskEmbedShell();
    },

    async refreshVenueCameraHostStatus() {
        this.venueCameraHostStatus = await fetchVenueCameraHostStatus({ force: true });
        return this.venueCameraHostStatus;
    },

    venuePcCameraAvailable() {
        const s = this.venueCameraHostStatus;
        return !!(s?.configured && s?.hostOnline && s?.capturing);
    },

    kioskCameraSourceLabel() {
        return this.kioskCameraSource === 'venue-pc' ? 'Venue PC camera' : 'Tablet camera';
    },

    async teardownLocalMediaForSourceSwitch() {
        this.stopLocalAudioMeter();
        if (this.localStream) {
            for (const track of this.localStream.getTracks()) track.stop();
            this.localStream = null;
        }
        await closeVenuePcBridge(this);
        if (this.peer) {
            try { this.peer.close(); } catch { /* ignore */ }
            this.peer = null;
        }
        this.remoteDescriptionApplied = false;
        this.answerSent = false;
        this.appliedOfferSdp = '';
        this.appliedAnswerSdp = '';
        this.seenOfferCandidates.clear();
        this.seenAnswerCandidates.clear();
        this.webrtcReady = false;
        this.attachStreams();
    },

    async setKioskCameraSourceAndReconnect(source) {
        const next = normalizeKioskCameraSource(source);
        if (next === 'venue-pc' && !this.venuePcCameraAvailable()) {
            this.$store.toastr.error(
                'Venue PC camera is offline — open Venue Control on the venue PC or use the tablet camera.',
                7000,
            );
            return;
        }
        if (this.cameraSourceSwitchBusy || this.mediaBusy) return;
        const prev = this.kioskCameraSource;
        if (next === prev && localStreamReadyForSource(this, next)) return;

        this.cameraSourceSwitchBusy = true;
        this.kioskCameraSource = next;
        this._localCameraAutoStartAttempted = false;
        this._localCameraPermissionDenied = false;
        try {
            await this.teardownLocalMediaForSourceSwitch();
            await this.openMedia({ source: next, force: true });
            persistKioskCameraSource(next);
            if (this.isRemoteChallenge() && this.session) {
                if (this.canConnectSession() || this.mySessionParticipant()?.connected) {
                    await this.registerSessionConnect(true);
                } else {
                    await this.syncPeerFromSession();
                }
            }
        } catch (e) {
            this.kioskCameraSource = prev;
            this.$store.toastr.error(e?.message || 'Unable to switch camera source', 5000);
            if (prev !== next) {
                try {
                    await this.teardownLocalMediaForSourceSwitch();
                    this.kioskCameraSource = prev;
                    await this.openMedia({ source: prev, force: true });
                } catch {
                    /* leave cleared */
                }
            }
        } finally {
            this.cameraSourceSwitchBusy = false;
        }
    },

    showKioskCameraSourcePicker() {
        return this.isVenueKioskEmbed() && (this.challengeLobbyShell() || this.isMatchLive());
    },

    tvPublicUrl() {
        return `${preferredAppOrigin()}/tv`;
    },

    isLocalChallenge() {
        const mode = String(this.challenge?.mode || '').trim().toLowerCase();
        return mode === 'local';
    },

    isRemoteChallenge() {
        const mode = String(this.challenge?.mode || '').trim().toLowerCase();
        if (mode === 'remote' || mode === 'online') return true;
        const stream = this.challenge?.stream;
        return !!(stream && String(stream.protocol || '').trim().toLowerCase() === 'webrtc');
    },

    canPairTvKiosk() {
        if (!this.isLocalChallenge()) return false;
        const admin = this.$store.session?.me?.role === 'admin';
        return admin || this.isParticipant();
    },

    showChallengeScorerQrStrip() {
        if (!this.challenge?._id || this.scoringWindow) return false;
        const admin = this.$store.session?.me?.role === 'admin';
        if (!this.isParticipant() && !admin) return false;
        if (this.isLocalChallenge()) {
            return !['declined', 'cancelled'].includes(String(this.challenge?.status || ''));
        }
        return this.$store.features.enabled('challengeSessions') && this.isRemoteChallenge();
    },

    showChallengeStickyBackRow() {
        return !this.isMatchLive() || this.isLocalChallenge();
    },

    showChallengeDevicesRow() {
        if (this.challengeMatchConsoleShell()) return false;
        return this.showChallengeScorerQrStrip()
            || (this.canPairTvKiosk() && !this.scoringWindow);
    },

    /** Remote board camera column; local challenges never use it */
    liveShowBoardVideoPanel() {
        if (this.isLocalChallenge()) return false;
        if (this.liveVideoPanelSuppressed) return false;
        if (this.stageShouldShowCamera()) return true;
        return this.liveVideoPanelExpanded;
    },

    canToggleRemoteVideoPanel() {
        return this.isRemoteChallenge()
            && this.isMatchLive()
            && this.$store.features.enabled('challengeSessions');
    },

    toggleLiveVideoPanel() {
        if (this.stageShouldShowCamera()) {
            this.liveVideoPanelSuppressed = !this.liveVideoPanelSuppressed;
            if (!this.liveVideoPanelSuppressed) this.liveVideoPanelExpanded = false;
        } else {
            this.liveVideoPanelExpanded = !this.liveVideoPanelExpanded;
            if (this.liveVideoPanelExpanded) this.liveVideoPanelSuppressed = false;
        }
    },

    liveVideoPanelToggleLabel() {
        if (!this.liveShowBoardVideoPanel()) return 'Show board camera';
        return this.stageShouldShowCamera() ? 'Hide camera' : 'Hide panel';
    },

    isChallengeConsoleSurface() {
        return this.surface === 'console' || this.surface === 'matchConsole' || this.surface === 'lobby';
    },

    /** Venue-kiosk embed shell (match console or camera lobby). */
    challengeKioskEmbedShell() {
        return this.surface === 'matchConsole' || this.surface === 'lobby';
    },

    /** Dedicated camera lobby route — session setup only, no scorer UI. */
    challengeLobbyShell() {
        return this.surface === 'lobby';
    },

    /** Fixture `/fixtures/:id/score`-style shell (light Match Console) */
    challengeMatchConsoleShell() {
        return this.surface === 'matchConsole';
    },

    challengeLegsPairFromResult() {
        const raw = String(this.challengeResult() || '0-0');
        const m = raw.match(/(\d+)\s*[-–:]\s*(\d+)/);
        if (m) return { ch: Number(m[1]), op: Number(m[2]) };
        return { ch: 0, op: 0 };
    },

    challengeMatchConsoleBack() {
        if (this.embedMode && this.fromVenueKiosk) {
            this.postVenueKioskShellMessage({
                type: this.challengeLobbyShell() ? 'challenge-lobby-leave' : 'challenge-scorer-leave',
            });
            return;
        }
        routerNavigateChallenge('/admin/challenges');
    },

    postVenueKioskShellMessage(payload) {
        try {
            if (globalThis.parent && globalThis.parent !== globalThis) {
                globalThis.parent.postMessage({ source: 'challenge-scorer-embed', ...payload }, globalThis.location.origin);
            }
        } catch { /* ignore */ }
    },

    isVenueKioskEmbed() {
        return !!(this.embedMode && this.fromVenueKiosk);
    },

    ensureVenueKioskRemoteLobby() {
        if (!this.isVenueKioskEmbed() || !this.isRemoteChallenge()) return;
        if (this.challengeLobbyShell()) {
            this.sessionSetupOpen = true;
            this.$nextTick(() => {
                this.$refs?.sessionSetup?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
            });
            void this.scheduleLobbyCameraAutoStart();
            return;
        }
        let lobbyRequested = false;
        try {
            lobbyRequested = new URL(globalThis.location.href).searchParams.get('lobby') === '1';
        } catch {
            lobbyRequested = false;
        }
        if (lobbyRequested || !this.isMatchLive()) {
            this.sessionSetupOpen = true;
            this.$nextTick(() => {
                this.$refs?.sessionSetup?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
            });
        }
    },

    maybeNotifyVenueKioskLobbyReady() {
        if (!this.challengeLobbyShell() || !this.isVenueKioskEmbed()) return;
        if (String(this.session?.status || '') !== 'live') return;
        this.postVenueKioskShellMessage({
            type: 'challenge-lobby-ready',
            challengeId: String(this.challenge?._id || ''),
        });
    },

    challengeScoringControllerPillVisible() {
        return this.isLocalChallenge() && this.isMatchLive();
    },

    activeChallengeScoringControllerDisplayLine() {
        const c = this.currentChallengeScoringControl();
        if (!c) return '';
        return String(c.userName || c.label || c.deviceId || '').trim() || 'Another device';
    },

    challengeMatchConsoleCenterLabel() {
        if (this.challenge?.liveScoring?.matchFinished || ['submitted', 'confirmed'].includes(String(this.challenge?.status || ''))) {
            return 'Result';
        }
        if (this.isMatchLive()) {
            return `Leg ${this.liveScoring().currentLegNumber || 1}`;
        }
        return 'Head to head';
    },

    challengeMatchConsolePhaseLine() {
        const s = String(this.challenge?.status || '');
        if (s === 'pending') return 'Awaiting response';
        if (s === 'accepted' && !this.isMatchLive()) return 'Ready to start';
        if (this.isMatchLive()) return 'Live';
        if (['submitted', 'disputed'].includes(s)) return s.charAt(0).toUpperCase() + s.slice(1);
        return this.modeLabel();
    },

    challengerPlayerIdStr() {
        return String(this.challenge?.challengerPlayerId?._id || this.challenge?.challengerPlayerId || '');
    },

    challengedPlayerIdStr() {
        return String(this.challenge?.challengedPlayerId?._id || this.challenge?.challengedPlayerId || '');
    },

    /** Live leg with a known current thrower — used for scoreboard ring glow */
    challengeMatchConsoleTurnHighlightActive() {
        return this.isMatchLive()
            && !this.challenge?.liveScoring?.matchFinished
            && !!this.currentTurnPlayerId();
    },

    challengeMatchConsoleChallengerTurnClass() {
        if (!this.challengeMatchConsoleTurnHighlightActive()) return '';
        const t = this.currentTurnPlayerId();
        if (t === this.challengerPlayerIdStr()) {
            return 'z-10 scale-[1.02] ring-4 ring-emerald-400 ring-offset-2 ring-offset-white shadow-[0_0_40px_rgba(16,185,129,0.55),0_0_0_1px_rgba(5,150,105,0.35)] dark:ring-emerald-400/90 dark:ring-offset-neutral-950 dark:shadow-[0_0_48px_rgba(16,185,129,0.35)]';
        }
        if (t === this.challengedPlayerIdStr()) {
            return 'opacity-[0.7] saturate-[0.88]';
        }
        return '';
    },

    challengeMatchConsoleOpponentTurnClass() {
        if (!this.challengeMatchConsoleTurnHighlightActive()) return '';
        const t = this.currentTurnPlayerId();
        if (t === this.challengedPlayerIdStr()) {
            return 'z-10 scale-[1.02] ring-4 ring-sky-400 ring-offset-2 ring-offset-white shadow-[0_0_40px_rgba(56,189,248,0.55),0_0_0_1px_rgba(14,165,233,0.35)] dark:ring-sky-400/90 dark:ring-offset-neutral-950 dark:shadow-[0_0_48px_rgba(56,189,248,0.35)]';
        }
        if (t === this.challengerPlayerIdStr()) {
            return 'opacity-[0.7] saturate-[0.88]';
        }
        return '';
    },

    challengeMatchConsoleWhoseTurnLine() {
        if (!this.challengeMatchConsoleTurnHighlightActive()) return '';
        if (this.currentTurnPlayerId() === this.challengerPlayerIdStr()) {
            return `${this.playerLabel(this.challenge?.challengerPlayerId)} to throw`;
        }
        if (this.currentTurnPlayerId() === this.challengedPlayerIdStr()) {
            return `${this.playerLabel(this.challenge?.challengedPlayerId)} to throw`;
        }
        return '';
    },

    challengeLiveHeaderChallengerCellClass() {
        if (!this.challengeMatchConsoleTurnHighlightActive()) return '';
        return this.currentTurnPlayerId() === this.challengerPlayerIdStr()
            ? 'ring-2 ring-emerald-400/90 ring-inset shadow-[inset_0_0_24px_rgba(16,185,129,0.25)]'
            : 'opacity-80';
    },

    challengeLiveHeaderOpponentCellClass() {
        if (!this.challengeMatchConsoleTurnHighlightActive()) return '';
        return this.currentTurnPlayerId() === this.challengedPlayerIdStr()
            ? 'ring-2 ring-sky-400/90 ring-inset shadow-[inset_0_0_24px_rgba(56,189,248,0.25)]'
            : 'opacity-80';
    },

    liveScoringOuterGridClass() {
        const split = !this.isLocalChallenge() && this.liveShowBoardVideoPanel();
        if (this.challengeMatchConsoleShell() && !split) {
            return 'grid grid-cols-1 gap-5 p-5 lg:grid-cols-12 lg:gap-5';
        }
        if (this.isChallengeConsoleSurface()) {
            if (split) {
                return 'grid gap-px bg-white/5 md:grid-cols-[minmax(0,1fr)_minmax(17rem,22rem)]';
            }
            // Full-width stack: visits / video column then keypad (matches usable width, not a narrow sidebar)
            return 'grid grid-cols-1 gap-px bg-white/5';
        }
        if (split) {
            return this.isDedicatedChallengeSurface()
                ? 'grid min-h-0 flex-1 gap-px bg-white/5 md:grid-cols-[minmax(0,1fr)_minmax(17rem,22rem)]'
                : 'grid gap-px bg-white/5 md:grid-cols-[minmax(0,1fr)_minmax(17rem,22rem)]';
        }
        if (this.isDedicatedChallengeSurface()) {
            return 'grid min-h-[min(520px,65dvh)] flex-1 grid-cols-1 gap-px bg-white/5 lg:min-h-0 lg:grid-cols-12';
        }
        return 'grid gap-px bg-white/5 lg:grid-cols-12';
    },

    liveScoringLeftColClass() {
        const split = !this.isLocalChallenge() && this.liveShowBoardVideoPanel();
        if (this.challengeMatchConsoleShell() && !split) {
            return 'flex min-h-0 min-w-0 flex-col gap-5 rounded-2xl border border-slate-200 bg-white/95 p-0 shadow-sm dark:border-neutral-800 dark:bg-neutral-950/90 lg:col-span-9';
        }
        const base = 'flex min-h-0 min-w-0 flex-col bg-neutral-950';
        if (this.isChallengeConsoleSurface()) {
            if (split) return base;
            return `${base} w-full min-w-0`;
        }
        if (split) return base;
        if (this.surface === 'scorer') {
            return `${base} order-2 min-h-0 flex-1 lg:order-1 lg:col-span-5`;
        }
        return `${base} lg:col-span-5`;
    },

    liveScoringAsideClass() {
        const split = !this.isLocalChallenge() && this.liveShowBoardVideoPanel();
        if (this.challengeMatchConsoleShell() && !split) {
            return 'flex min-h-0 min-w-0 flex-col gap-5 lg:col-span-3';
        }
        const base = 'flex min-h-0 min-w-0 flex-col divide-y divide-white/5 bg-neutral-950';
        if (this.isChallengeConsoleSurface()) {
            if (split) return `${base} min-h-0`;
            return `${base} w-full min-w-0`;
        }
        if (split) return `${base} min-h-0`;
        if (this.surface === 'scorer') {
            return `${base} order-1 min-h-0 flex-1 lg:order-2 lg:col-span-7`;
        }
        return `${base} lg:col-span-7`;
    },

    applySurfaceFromPath() {
        this.surface = challengeSurfaceFromPath();
        this.scoringWindow = this.surface === 'scorer';
        applyDedicatedChromeMode(this.scoringWindow);
    },

    isDedicatedChallengeSurface() {
        return this.surface === 'scorer';
    },

    /** Live scoring card visibility (dedicated routes must not hide behind session-setup gate) */
    challengeLiveScoringVisible() {
        if (this.challengeLobbyShell()) return false;
        const ls = this.challenge?.liveScoring;
        const matchFinished = !!ls?.matchFinished;
        const sessionAllowsPanel = !this.session || String(this.session?.status || '') === 'live' || matchFinished;

        if (this.isDedicatedChallengeSurface()) {
            if (['declined', 'cancelled'].includes(String(this.challenge?.status || ''))) return false;
            // Dedicated /scorer must always show the board (keypad, visits, camera prompts). Previously we hid the
            // whole section when a remote session existed but was not "live" yet — only the sticky header remained.
            return true;
        }
        if (!sessionAllowsPanel) return false;
        if (this.isMatchLive() && this.showSessionSetup()) return false;
        return true;
    },

    liveKeypadCenteredClass() {
        if (this.isChallengeConsoleSurface()) return 'w-full min-w-0 max-w-none';
        if (this.surface === 'scorer') return 'mx-auto w-full max-w-xl lg:max-w-2xl';
        if (this.isLocalChallenge()) return 'mx-auto w-full max-w-xl lg:max-w-2xl';
        return 'w-full min-w-0 max-w-none';
    },

    liveStatsCenteredClass() {
        if (this.isChallengeConsoleSurface()) return 'w-full min-w-0 max-w-none';
        if (this.surface === 'scorer') return 'mx-auto w-full max-w-2xl';
        if (this.isLocalChallenge()) return 'mx-auto w-full max-w-xl lg:max-w-2xl';
        return 'w-full min-w-0 max-w-none';
    },

    async pairTvKiosk() {
        this.tvKioskPairError = '';
        this.tvKioskPairMessage = '';
        const code = String(this.tvKioskPairCode || '').trim().toUpperCase();
        const id = String(this.challenge?._id || '');
        if (!code || !id) return;
        this.tvKioskPairBusy = true;
        try {
            const res = await apiFetch('/tv-sessions/pair-challenge', {
                method: 'POST',
                body: { pairCode: code, challengeMatchId: id },
            });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Pair failed');
            this.tvKioskPairMessage = 'TV paired. The kiosk should show this challenge shortly.';
            this.tvKioskPairCode = '';
            this.tvKioskLinkedActive = true;
        } catch (e) {
            this.tvKioskPairError = e.message || 'Pair failed';
        } finally {
            this.tvKioskPairBusy = false;
        }
    },

    canBroadcastLive() {
        return this.isLocalChallenge() && this.isMatchLive() && !!this.challenge?._id;
    },

    broadcastLiveActive() {
        const session = this.broadcastSession;
        return !!(session && session.status === 'live' && String(session.matchId) === String(this.challenge?._id || ''));
    },

    async refreshBroadcastStatus() {
        if (!this.canBroadcastLive()) {
            this.broadcastSession = null;
            this.broadcastWatchUrl = '';
            this.broadcastYoutubeActive = false;
            return;
        }
        try {
            const session = await fetchActiveBroadcastSession();
            if (session && String(session.matchId) === String(this.challenge?._id || '')) {
                this.broadcastSession = session;
                this.broadcastWatchUrl = buildSpectatorWatchUrl(
                    { ...session, watchToken: session.watchToken },
                    { origin: preferredAppOrigin() },
                );
            } else {
                this.broadcastSession = null;
                this.broadcastWatchUrl = '';
            }
            try {
                const [host, settings] = await Promise.all([
                    fetchBroadcastHostStatus(),
                    fetchBroadcastSettings(),
                ]);
                this.broadcastYoutubeActive = !!host?.stream?.youtubeEnabled;
                this.broadcastYoutubeConfigured = !!settings?.streamKeyConfigured;
            } catch {
                this.broadcastYoutubeActive = false;
            }
        } catch {
            this.broadcastSession = null;
            this.broadcastWatchUrl = '';
            this.broadcastYoutubeActive = false;
        }
    },

    goLivePinConfigured() {
        return isVenueAdminPinConfigured();
    },

    openGoLivePinDialog(action) {
        if (!this.goLivePinConfigured()) {
            this.broadcastError =
                'Operator passcode is not configured. Set VITE_VENUE_KIOSK_ADMIN_PIN in venue.env and restart Local API.';
            return;
        }
        this._goLivePinPendingAction = String(action || '');
        this.goLivePinInput = '';
        this.goLivePinError = '';
        this.goLivePinDialogOpen = true;
        Alpine.nextTick(() => {
            globalThis.document?.querySelector('[x-model="goLivePinInput"]')?.focus?.();
        });
    },

    closeGoLivePinDialog() {
        this.goLivePinDialogOpen = false;
        this.goLivePinInput = '';
        this.goLivePinError = '';
        this._goLivePinPendingAction = '';
    },

    submitGoLivePin() {
        const pin = String(this.goLivePinInput || '').replace(/\D/g, '').slice(0, 6);
        if (!verifyVenueAdminPin(pin)) {
            this.goLivePinError = 'Invalid passcode';
            Alpine.nextTick(() => {
                const el = globalThis.document?.querySelector('[x-model="goLivePinInput"]');
                el?.focus?.();
                el?.select?.();
            });
            return;
        }
        const action = this._goLivePinPendingAction;
        this.closeGoLivePinDialog();
        if (action === 'lan-start') void this.startBroadcastLiveConfirmed();
        else if (action === 'youtube-start') void this.startYoutubeStreamConfirmed();
        else if (action === 'youtube-stop') void this.stopYoutubeStreamConfirmed();
    },

    promptStartBroadcastLive() {
        this.openGoLivePinDialog('lan-start');
    },

    broadcastYoutubeStreamActive() {
        return this.broadcastLiveActive() && this.broadcastYoutubeActive;
    },

    canStartYoutubeStream() {
        return this.broadcastLiveActive() && this.broadcastYoutubeConfigured && !this.broadcastYoutubeStreamActive();
    },

    youtubeStreamButtonVisible() {
        return this.broadcastYoutubeConfigured && !this.broadcastYoutubeStreamActive();
    },

    async startBroadcastLive() {
        this.promptStartBroadcastLive();
    },

    async startBroadcastLiveConfirmed() {
        if (!this.canBroadcastLive() || this.broadcastBusy) return;
        this.broadcastBusy = true;
        this.broadcastError = '';
        this.broadcastMessage = '';
        try {
            const session = await ensureBroadcastSessionForMatch({
                matchType: 'challenge',
                matchId: String(this.challenge._id),
            });
            this.broadcastSession = session;
            await ensureBroadcastHostWindow();
            const host = await waitForBroadcastHostCapturing();
            this.broadcastWatchUrl = buildSpectatorWatchUrl(session, { origin: preferredAppOrigin() });
            const encoderMsg = broadcastHostEncoderMessage(host);
            this.broadcastMessage = encoderMsg || 'Live on LAN — share the watch link or pair a TV in Live mode.';
        } catch (e) {
            const msg = String(e?.message || '');
            this.broadcastError = msg === 'broadcast_already_active'
                ? 'Another match is already live — stop it in Venue Control → Live broadcast.'
                : (msg || 'Unable to start live broadcast');
        } finally {
            this.broadcastBusy = false;
            void this.refreshBroadcastStatus();
        }
    },

    promptStartYoutubeStream() {
        this.openGoLivePinDialog('youtube-start');
    },

    promptStopYoutubeStream() {
        this.openGoLivePinDialog('youtube-stop');
    },

    async startYoutubeStreamConfirmed() {
        if (!this.broadcastLiveActive() || this.broadcastBusy) {
            this.broadcastError = 'Start LAN live first.';
            return;
        }
        this.broadcastBusy = true;
        this.broadcastError = '';
        try {
            await setBroadcastYoutubeEnabled(true);
            await requestBroadcastHostWake().catch(() => {});
            await waitForBroadcastHostCapturing({ attempts: 15, delayMs: 1000 });
            this.broadcastMessage = 'YouTube live started.';
        } catch (e) {
            this.broadcastError = e?.message || 'Unable to start YouTube stream';
        } finally {
            this.broadcastBusy = false;
            void this.refreshBroadcastStatus();
        }
    },

    async stopYoutubeStreamConfirmed() {
        if (this.broadcastBusy) return;
        this.broadcastBusy = true;
        try {
            await setBroadcastYoutubeEnabled(false);
            this.broadcastMessage = 'YouTube stopped — LAN live continues.';
        } catch (e) {
            this.broadcastError = e?.message || 'Unable to stop YouTube stream';
        } finally {
            this.broadcastBusy = false;
            void this.refreshBroadcastStatus();
        }
    },

    async stopBroadcastLive() {
        if (this.broadcastBusy) return;
        this.broadcastBusy = true;
        this.broadcastError = '';
        try {
            await this.refreshBroadcastStatus();
            let sessionId = this.broadcastSession?.id;
            if (!sessionId) {
                const active = await fetchActiveBroadcastSession();
                if (active && String(active.matchId) === String(this.challenge?._id || '')) {
                    sessionId = active.id;
                    this.broadcastSession = active;
                }
            }
            if (!sessionId) {
                this.broadcastError = 'No active broadcast for this match.';
                return;
            }
            await stopBroadcastSession(sessionId);
            this.broadcastSession = null;
            this.broadcastWatchUrl = '';
            this.broadcastMessage = 'Live broadcast stopped.';
        } catch (e) {
            this.broadcastError = e?.message || 'Unable to stop broadcast';
        } finally {
            this.broadcastBusy = false;
        }
    },

    async refreshTvKioskLinkStatus() {
        const id = String(this.challenge?._id || '');
        if (!id) {
            this.tvKioskLinkedActive = false;
            return;
        }
        if (!this.isLocalChallenge() || !this.canPairTvKiosk()) {
            this.tvKioskLinkedActive = false;
            return;
        }
        try {
            const res = await apiFetch(`/tv-sessions/challenge/${encodeURIComponent(id)}/link`);
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.ok) {
                this.tvKioskLinkedActive = false;
                return;
            }
            this.tvKioskLinkedActive = !!data.linked;
        } catch {
            this.tvKioskLinkedActive = false;
        }
    },

    currentPlayerId() {
        return rosterPlayerIdFromSession(this.$store.session);
    },

    isMobileViewport() {
        return window.matchMedia?.('(max-width: 639px)')?.matches ?? window.innerWidth < 640;
    },

    challengeConsoleUrl() {
        const id = this.challenge?._id || challengeIdFromPath();
        return id ? `/admin/challenges/${id}/score` : '/admin/challenges';
    },

    /** Absolute URL for QR codes and sharing — same origin rules as fixture scorer (`preferredAppOrigin` + VITE_DEV_PUBLIC_ORIGIN on localhost). */
    challengeScorerUrl() {
        const id = this.challenge?._id || challengeIdFromPath();
        if (!id) return '';
        const base = preferredAppOrigin();
        try {
            return new URL(`/admin/challenges/${id}/scorer`, base).href;
        } catch {
            return `${base}/admin/challenges/${id}/scorer`;
        }
    },

    /** Same-tab / same-app navigation (matches fixture board — avoid preferredAppOrigin() pointing at another dev port). */
    challengeBoardUrl() {
        const id = this.challenge?._id || challengeIdFromPath();
        return id ? `/admin/challenges/${id}/board` : '';
    },

    async reload() {
        this.applySurfaceFromPath();
        const id = challengeIdFromPath();
        if (!id) {
            this.loading = false;
            return;
        }
        if (this.surface === 'console') {
            this.loading = false;
            routerNavigateChallenge(`/admin/challenges/${id}/score`);
            return;
        }
        // x-if on the console tears down the QR mount; drop canvas so we attach a fresh one after load
        this.scorerQrCanvas = null;
        this.loading = true;
        try {
            this.scoringDeviceId = makeChallengeScoringDeviceId(id);
            this.scoringDeviceLabel = `${navigator.platform || 'Device'} challenge`;
            await this.refreshChallenge(id);
            this.ensureChallengeEventStream();
            await this.loadSession();
            this.ensureVenueKioskRemoteLobby();
            this.maybeNotifyVenueKioskLobbyReady();
            await this.maybeAutoStartLocalCameraPreview();
            this.ensureHeartbeatLoop();
            if (this.isLocalChallenge() && this.scoringWindow && this.isMatchLive()
                && (this.isParticipant() || this.$store.session?.me?.role === 'admin')) {
                await this.ensureChallengeScoringClaim(false);
            }
            this.ensureChallengeScoringHeartbeatLoop();
            await this.refreshTvKioskLinkStatus();
            await this.refreshBroadcastStatus();
            this.$nextTick(() => {
                this.renderScorerQr();
            });
            this.maybeToastPendingChallengeNotice();
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to load challenge');
        } finally {
            this.loading = false;
        }
    },

    maybeToastPendingChallengeNotice() {
        const id = String(this.challenge?._id || '');
        if (!id || String(this.challenge?.status || '') !== 'pending') return;
        if (this._pendingChallengeNoticeToastId === id) return;
        const t = this.$store.toastr;
        if (this.challengePendingWaitingOnOpponent()) {
            const opp = this.playerLabel(this.challenge.challengedPlayerId);
            t.info(`${opp} must accept or reject this challenge before play can start.`, 6500);
            this._pendingChallengeNoticeToastId = id;
            return;
        }
        if (this.canRespond()) {
            if (this.isChallengedPlayer()) {
                t.info('This challenge needs your response — use Accept or Reject in the top bar.', 6000);
            } else {
                t.info('This challenge is pending — use Accept or Reject in the top bar.', 6000);
            }
            this._pendingChallengeNoticeToastId = id;
        }
    },

    async refreshChallenge(id = this.challenge?._id || challengeIdFromPath()) {
        if (!id) return;
        const prevId = String(this.challenge?._id || '');
        const res = await apiFetch(`/challenges/${id}`);
        const data = await res.json();
        if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to load challenge');
        this.challenge = data.challenge;
        const newId = String(this.challenge?._id || '');
        if (prevId && prevId !== newId) {
            this.liveVideoPanelExpanded = false;
            this.liveVideoPanelSuppressed = false;
            this._pendingChallengeNoticeToastId = '';
            this._localCameraAutoStartAttempted = false;
            this._localCameraPermissionDenied = false;
            this.stopLobbyCameraAutoStart();
        }
        this.scoreRows = this.buildScoreRows();
        this.syncSessionUiState();
        this.syncFocusState();
        this.syncFocusState();
    },

    syncSessionUiState() {
        const currentStatus = String(this.session?.status || '');
        const previousStatus = String(this.lastSessionStatus || '');
        this.lastSessionStatus = currentStatus;
        if (!this.session) return;
        if (currentStatus === 'live' && previousStatus !== 'live') {
            this.sessionSetupOpen = false;
            if (this.challengeLobbyShell() && this.isVenueKioskEmbed()) {
                this.postVenueKioskShellMessage({
                    type: 'challenge-lobby-ready',
                    challengeId: String(this.challenge?._id || ''),
                });
            }
        }
    },

    buildScoreRows() {
        const players = this.challenge?.scoring?.players || [];
        return players.map((playerScore) => {
            const hydrated = playerScore?.playerId && typeof playerScore.playerId === 'object' ? playerScore.playerId : null;
            return scoreRowSeed({ ...playerScore, _id: hydrated?._id, fullName: hydrated?.fullName, nickname: hydrated?.nickname, imageUrl: hydrated?.imageUrl, isGuest: hydrated?.isGuest });
        });
    },

    async renderScorerQr() {
        if (!this.challenge?._id || this.isMobileViewport()) return;
        const data = this.challengeScorerUrl();
        const mounts = [this.$refs?.scorerQr, this.$refs?.scorerQrAside].filter(Boolean);
        if (!mounts.length) return;

        const paint = async (canvas) => {
            await QRCode.toCanvas(canvas, data, {
                width: 144,
                margin: 1,
                color: { dark: '#000000', light: '#ffffff' },
                errorCorrectionLevel: 'H',
            });
        };

        try {
            for (const mount of mounts) {
                let canvas = mount.querySelector?.('canvas');
                if (!canvas) {
                    mount.innerHTML = '';
                    canvas = document.createElement('canvas');
                    canvas.width = 144;
                    canvas.height = 144;
                    canvas.className = 'block h-36 w-36 rounded-md';
                    mount.appendChild(canvas);
                }
                await paint(canvas);
            }
            this.scorerQrCanvas = mounts[0]?.querySelector?.('canvas') || null;
        } catch (error) {
            console.error('Failed to render challenge scorer QR', error);
        }
    },

    openScorerWindow() {
        if (!this.challenge?._id) return;
        const url = this.challengeScorerUrl();
        const handle = window.open(url, `pdl-challenge-scorer-${this.challenge._id}`, 'popup=yes,width=1440,height=980,resizable=yes,scrollbars=yes');
        // iPad / iOS Safari often blocks popups — same-tab navigation still opens the score controller.
        if (!handle) {
            try {
                window.location.assign(url);
            } catch {
                window.location.href = url;
            }
        }
    },

    openBoardWindow() {
        if (!this.challenge?._id) return;
        const url = this.challengeBoardUrl();
        const handle = window.open(url, `pdl-challenge-board-${this.challenge._id}`, 'popup=yes,width=1600,height=900,resizable=yes,scrollbars=yes');
        if (!handle) {
            try {
                window.location.assign(url);
            } catch {
                window.location.href = url;
            }
        }
    },

    async castChallengeBoard() {
        const path = this.challengeBoardUrl();
        if (!path) return;
        let boardUrl;
        try {
            boardUrl = new URL(path, window.location.origin).href;
        } catch {
            boardUrl = path;
        }
        try {
            if ('PresentationRequest' in window) {
                const request = new window.PresentationRequest([boardUrl]);
                await request.start();
                this.$store.toastr.success('Challenge board cast started', 2500);
                return;
            }
            if (navigator.share) {
                await navigator.share({
                    title: this.challenge?.title || 'Challenge board',
                    text: 'Open the live challenge board',
                    url: boardUrl,
                });
                return;
            }
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(boardUrl);
                this.$store.toastr.success('Challenge board link copied', 2500);
                return;
            }
            window.open(boardUrl, '_blank', 'noopener');
        } catch (e) {
            if (String(e?.name || '') === 'AbortError') return;
            this.$store.toastr.error('Unable to cast challenge board', 3000);
        }
    },

    challengeEventUrl() {
        const token = encodeURIComponent(getAccessToken() || '');
        const apiBase = String(window.__API_BASE__ || '').replace(/\/+$/, '');
        const path = `/api/challenges/${this.challenge?._id}/events?access_token=${token}`;
        return apiBase ? `${apiBase}${path}` : path;
    },

    ensureChallengeEventStream() {
        if (this.challengeEventSource || !this.challenge?._id || !window.EventSource) return;
        try {
            this.challengeEventSource = new EventSource(this.challengeEventUrl());
            const handler = (event) => {
                const payload = JSON.parse(event.data || '{}');
                this.challengeStreamHealthy = true;
                if (payload?.challenge) {
                    this.challenge = payload.challenge;
                    this.scoreRows = this.buildScoreRows();
                    this.syncFocusState();
                    this.ensureChallengeScoringHeartbeatLoop();
                }
            };
            for (const evt of ['challenge:update', 'challenge:status', 'challenge:score', 'challenge:live-score', 'challenge:undo', 'challenge:next-leg']) {
                this.challengeEventSource.addEventListener(evt, handler);
            }
            this.challengeEventSource.addEventListener('challenge:removed', () => {
                this.challengeStreamHealthy = true;
                this.handleChallengeRemoved();
            });
            this.challengeEventSource.onerror = () => {
                this.challengeStreamHealthy = false;
            };
        } catch {
            this.challengeStreamHealthy = false;
        }
    },

    disconnectChallengeEventStream() {
        if (this.challengeEventSource) {
            this.challengeEventSource.close();
            this.challengeEventSource = null;
        }
        this.challengeStreamHealthy = false;
    },

    ensureChallengePollingLoop() {
        if (this.challengePollTimer || !this.challenge?._id) return;
        this.challengePollTimer = window.setInterval(async () => {
            if (!this.isMatchLive() || this.challengeStreamHealthy) return;
            try {
                await this.refreshChallenge();
            } catch { }
        }, 3000);
    },

    stopChallengePollingLoop() {
        if (this.challengePollTimer) {
            window.clearInterval(this.challengePollTimer);
            this.challengePollTimer = null;
        }
    },

    async loadSession() {
        if (!this.$store.features.enabled('challengeSessions') || !this.challenge?._id) {
            this.session = null;
            this.disconnectEventStream();
            this.stopSignalingLoop();
            return;
        }
        if (this.isLocalChallenge()) {
            this.session = null;
            this.disconnectEventStream();
            this.stopSignalingLoop();
            return;
        }
        this.sessionLoading = true;
        try {
            const res = await apiFetch(`/challenges/${this.challenge._id}/session`);
            const data = await res.json();
            if (!res.ok) throw new Error(data?.message || 'Failed to load session');
            this.session = data.session || null;
            if (!this.session && this.isRemoteChallenge() && this.isParticipant()) {
                await this.ensureSessionExists(true);
                return;
            }
            this.seedFallbackForm();
            this.syncSessionUiState();
            this.syncFocusState();
            if (this.isRemoteChallenge() && this.session) {
                await this.syncPeerFromSession();
            }
            if (this.session) {
                this.ensureEventStream();
            } else {
                this.disconnectEventStream();
                this.stopSignalingLoop();
            }
            await this.$nextTick();
            await this.maybeAutoStartLocalCameraPreview();
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to load challenge session');
        } finally {
            this.sessionLoading = false;
        }
    },

    async ensureSessionExists(silent = false) {
        const res = await apiFetch(`/challenges/${this.challenge._id}/session`, { method: 'POST', body: {} });
        const data = await res.json();
        if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to create challenge session');
        this.session = data.session || this.session;
        this.seedFallbackForm();
        this.syncSessionUiState();
        this.syncFocusState();
        if (this.isRemoteChallenge() && this.session) {
            await this.syncPeerFromSession();
        }
        if (this.session) {
            this.ensureEventStream();
        }
        await this.$nextTick();
        await this.maybeAutoStartLocalCameraPreview();
        if (!silent) this.$store.toastr.success('Challenge session ready', 3000);
    },

    mySessionParticipant() {
        const uid = this.currentPlayerId();
        return (this.session?.participants || []).find((item) => String(item.playerId?._id || item.playerId || '') === uid) || null;
    },

    seedFallbackForm() {
        const camera = this.session?.venueCamera || {};
        this.fallbackForm = {
            enabled: !!camera.enabled,
            sourceType: camera.sourceType || 'rtsp',
            sourceUrl: camera.sourceUrl || '',
            relayUrl: camera.relayUrl || '',
            publicViewerUrl: camera.publicViewerUrl || '',
            label: camera.label || '',
            notes: camera.notes || '',
        };
    },

    otherSessionParticipant() {
        const uid = this.currentPlayerId();
        return (this.session?.participants || []).find((item) => String(item.playerId?._id || item.playerId || '') !== uid) || null;
    },

    sessionStatusLabel() {
        const status = String(this.session?.status || 'awaiting_players');
        const map = {
            awaiting_players: 'Awaiting Players',
            camera_ready: 'Camera Ready',
            connecting: 'Connecting',
            ready: 'Ready',
            live: 'Live',
            ended: 'Ended',
            failed: 'Failed',
        };
        return map[status] || status.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    },

    sessionStatusClasses() {
        const status = String(this.session?.status || 'awaiting_players');
        if (status === 'live') {
            return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
        }
        if (status === 'awaiting_players') {
            return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
        }
        return statusMeta(status);
    },

    myChallengeCameraConnected() {
        return this.hasConnectedCamera();
    },

    opponentChallengeCameraConnected() {
        const other = this.otherSessionParticipant();
        return !!(other?.cameraEnabled || other?.connected || this.hasRemotePreview());
    },

    challengeCamerasStreaming() {
        return !!(this.webrtcReady || (this.hasLocalPreview() && this.hasRemotePreview()));
    },

    challengeCameraPillVisible() {
        return this.isRemoteChallenge()
            && this.$store.features.enabled('challengeSessions')
            && !!this.session;
    },

    challengeCameraPillLabel() {
        if (this.challengeCamerasStreaming()) return 'Streaming';
        const mine = this.myChallengeCameraConnected();
        const theirs = this.opponentChallengeCameraConnected();
        if (mine && theirs) return 'Connected';
        if (!mine && !theirs) return 'Connect cameras';
        if (!mine) return 'Waiting for your camera';
        return 'Waiting for opponent camera';
    },

    challengeCameraPillClasses() {
        if (this.challengeCamerasStreaming()) {
            return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
        }
        const mine = this.myChallengeCameraConnected();
        const theirs = this.opponentChallengeCameraConnected();
        if (mine && theirs) {
            return 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200';
        }
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
    },

    challengeResponsePillVisible() {
        return this.challengePendingWaitingOnOpponent();
    },

    canCreateSession() {
        return this.isParticipant() && !this.session && !this.isRemoteChallenge();
    },

    canConnectSession() {
        return this.isParticipant() && this.session && (!this.mySessionParticipant()?.connected || !this.hasLocalPreview());
    },

    canReconnectSession() {
        return this.isParticipant() && this.session && this.hasMediaSession() && (this.hasLocalPreview() || this.hasRemotePreview() || !!this.mySessionParticipant()?.connected);
    },

    otherPeerConnected() {
        return !!(this.otherSessionParticipant()?.connected || this.otherSessionParticipant()?.cameraEnabled || this.hasRemotePreview() || this.webrtcReady);
    },

    bothSessionPeersConnected() {
        return this.hasConnectedCamera() && this.otherPeerConnected();
    },

    isSessionReady() {
        return !!this.mySessionParticipant()?.readyAt;
    },

    canResumeSession() {
        return !!this.session && ['ended', 'failed'].includes(String(this.session?.status || ''));
    },

    canMarkSessionReady() {
        if (!this.isParticipant() || !this.session) return false;
        if (this.canResumeSession()) return false;
        if (String(this.session?.status || '') === 'live') return false;
        return !this.isSessionReady() && this.bothSessionPeersConnected();
    },

    readyButtonLabel() {
        if (String(this.session?.status || '') === 'live') return 'Match Live';
        if (this.canResumeSession()) return 'Resume';
        if (this.isSessionReady()) return this.otherSessionParticipant()?.readyAt ? 'Starting…' : 'Waiting for Opponent';
        return 'Ready';
    },

    sessionReadyHeadline() {
        if (this.isRemoteChallenge() && !this.hasConnectedCamera()) {
            return 'Connect your camera to get ready.';
        }
        if (this.isSessionReady()) return 'You are marked ready.';
        return 'Camera connected. Mark yourself ready.';
    },

    sessionReadySubtext() {
        if (this.isRemoteChallenge() && !this.hasConnectedCamera()) {
            return 'Your camera must be connected before the match can start.';
        }
        if (this.otherSessionParticipant()?.readyAt) {
            return `${this.playerLabel(this.otherSessionParticipant()?.playerId)} is ready as well.`;
        }
        return 'The match will begin automatically as soon as both players are ready.';
    },

    remoteUfrag(description = null) {
        const sdp = String(description?.sdp || '');
        const match = sdp.match(/a=ice-ufrag:([^\r\n]+)/);
        return match?.[1] || '';
    },

    candidateMatchesRemoteDescription(candidate, description = null) {
        const expected = this.remoteUfrag(description);
        const actual = String(candidate?.usernameFragment || '');
        if (!expected || !actual) return true;
        return expected === actual;
    },

    async addRemoteCandidate(candidate, bucket = 'answer') {
        const pc = this.peer;
        const remoteDescription = pc?.remoteDescription;
        if (!pc || !remoteDescription) return false;
        if (!this.candidateMatchesRemoteDescription(candidate, remoteDescription)) return false;
        try {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
            return true;
        } catch (error) {
            const message = String(error?.message || error || '');
            if (message.toLowerCase().includes('ufrag')) return false;
            throw error;
        }
    },

    showSessionSetup() {
        if (this.challengeLobbyShell()) return this.isRemoteChallenge();
        if (this.isLocalChallenge()) return false;
        if (!this.session) return true;
        if (String(this.session?.status || '') !== 'live') return true;
        return this.sessionSetupOpen;
    },

    toggleSessionSetup() {
        if (!this.session) return;
        if (String(this.session?.status || '') !== 'live') {
            this.sessionSetupOpen = true;
            return;
        }
        this.sessionSetupOpen = !this.sessionSetupOpen;
    },

    hasMediaSession() {
        return this.isRemoteChallenge() && !!this.session;
    },

    hasLocalPreview() {
        return (this.localStream?.getVideoTracks?.() || []).some((track) => track.readyState === 'live' && track.enabled !== false);
    },

    hasRemotePreview() {
        return (this.remoteStream?.getVideoTracks?.() || []).some((track) => track.readyState === 'live');
    },

    hasConnectedCamera() {
        return this.hasLocalPreview() || !!this.mySessionParticipant()?.cameraEnabled;
    },

    hasLiveMicrophone() {
        return (this.localStream?.getAudioTracks?.() || []).some((track) => track.readyState === 'live');
    },

    micButtonLabel() {
        return this.micMuted ? 'Unmute Mic' : 'Mute Mic';
    },

    micStatusLabel() {
        if (!this.hasLiveMicrophone()) return 'Mic unavailable';
        return this.micMuted ? 'Muted' : 'Mic live';
    },

    micStatusClasses() {
        if (!this.hasLiveMicrophone()) return 'bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200';
        return this.micMuted ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
    },

    localMicMeterStatusLabel() {
        if (!this.hasLiveMicrophone()) return 'Awaiting audio';
        return this.micMuted ? 'Muted' : 'Input live';
    },

    micMeterBars() {
        const scaled = Math.max(0, Math.min(1, Number(this.localMicLevel || 0)));
        return Array.from({ length: 12 }, (_, index) => scaled >= ((index + 1) / 12));
    },

    hasRemoteMicrophone() {
        return (this.remoteStream?.getAudioTracks?.() || []).some((track) => track.readyState === 'live');
    },

    shouldShowRemoteMicrophoneMeter() {
        return !!this.otherSessionParticipant()?.connected || this.hasRemoteMicrophone();
    },

    remoteMicMeterBars() {
        const scaled = Math.max(0, Math.min(1, Number(this.remoteMicLevel || 0)));
        return Array.from({ length: 12 }, (_, index) => scaled >= ((index + 1) / 12));
    },

    remoteMicStatusLabel() {
        if (this.hasRemoteMicrophone()) {
            return Number(this.remoteMicLevel || 0) > 0.03 ? 'Audio live' : 'Listening';
        }
        return this.otherSessionParticipant()?.connected ? 'Awaiting audio' : 'Not connected';
    },

    remotePeerLabel() {
        if (this.webrtcReady || this.hasRemotePreview()) return 'Peer video live';
        const liveConnectionState = String(this.peer?.connectionState || this.session?.diagnostics?.connectionState || '').toLowerCase();
        const liveIceState = String(this.peer?.iceConnectionState || this.session?.diagnostics?.iceConnectionState || '').toLowerCase();
        if (['connected', 'completed'].includes(liveConnectionState) || ['connected', 'completed'].includes(liveIceState)) {
            return 'Peer connected, awaiting video';
        }
        if (this.otherSessionParticipant()?.connected) return 'Opponent connected, linking video';
        return 'Waiting for peer';
    },

    peerFailureState() {
        const connectionState = String(this.session?.diagnostics?.connectionState || '').toLowerCase();
        const iceConnectionState = String(this.session?.diagnostics?.iceConnectionState || '').toLowerCase();
        return connectionState === 'failed' || iceConnectionState === 'failed';
    },

    peerDiagnosticsLabel() {
        if (this.webrtcReady || this.hasRemotePreview()) return 'Peer video live';
        return this.session?.diagnostics?.iceConnectionState || this.session?.diagnostics?.connectionState || 'Waiting for negotiation';
    },

    peerTransportLabel() {
        if (this.webrtcReady || this.hasRemotePreview()) {
            return this.session?.diagnostics?.relayUsed ? '(TURN relay)' : '(Direct peer)';
        }
        return this.session?.diagnostics?.turnConfigured ? '(TURN ready)' : '(STUN only)';
    },

    peerDebugRows() {
        const peer = this.peer;
        const signaling = this.session?.signaling || {};
        const role = this.isOfferer() ? 'challenger / offerer' : 'challenged / answerer';
        return [
            { label: 'Role', value: role },
            { label: 'Signal gen', value: String(signaling.generation ?? 0) },
            { label: 'Local desc', value: peer?.localDescription?.type || 'none' },
            { label: 'Remote desc', value: peer?.remoteDescription?.type || 'none' },
            { label: 'Conn state', value: String(peer?.connectionState || this.session?.diagnostics?.connectionState || 'new') },
            { label: 'ICE state', value: String(peer?.iceConnectionState || this.session?.diagnostics?.iceConnectionState || 'new') },
            { label: 'Offer cand', value: String((signaling.offerCandidates || []).length) },
            { label: 'Answer cand', value: String((signaling.answerCandidates || []).length) },
            { label: 'Remote tracks', value: String((this.remoteStream?.getVideoTracks?.() || []).length) },
        ];
    },

    showPeerFailureWarning() {
        if (!this.hasMediaSession() || !this.session || this.webrtcReady || this.hasRemotePreview()) return false;
        if (!this.mySessionParticipant()?.connected || !this.hasLocalPreview()) return false;
        if (!this.otherSessionParticipant()?.connected) return false;
        const peer = this.peer;
        const liveConnectionState = String(peer?.connectionState || '').toLowerCase();
        const liveIceState = String(peer?.iceConnectionState || '').toLowerCase();
        if (liveConnectionState && liveConnectionState !== 'failed' && liveConnectionState !== 'closed') return false;
        if (liveIceState && liveIceState !== 'failed' && liveIceState !== 'closed' && liveIceState !== 'disconnected') return false;
        return this.peerFailureState();
    },

    liveScoring() {
        return this.challenge?.liveScoring || {};
    },

    liveLegs() {
        return this.liveScoring().legs || [];
    },

    currentLiveLeg() {
        return this.liveLegs().find((leg) => !leg.complete) || this.liveLegs()[this.liveLegs().length - 1] || null;
    },

    currentTurnPlayerId() {
        return String(this.liveScoring().currentTurnPlayerId || '');
    },

    isMyTurn() {
        return this.currentPlayerId() === this.currentTurnPlayerId();
    },

    playerRemaining(playerId) {
        return Number(this.liveScoring()?.currentRemaining?.[String(playerId)] ?? this.liveScoring()?.startingScore ?? 501);
    },

    canUseLiveScoring() {
        const s = String(this.challenge?.status || '');
        if (['declined', 'cancelled', 'confirmed'].includes(s)) return false;
        if (this.$store.session?.me?.role === 'admin') return true;
        return this.isParticipant();
    },

    isMatchLive() {
        if (this.challenge?.liveScoring?.matchFinished) return false;
        if (this.session && this.hasMediaSession()) return String(this.session?.status || '') === 'live';
        return ['accepted', 'in_progress', 'submitted', 'disputed'].includes(String(this.challenge?.status || ''));
    },

    currentChallengeScoringControl() {
        const c = this.challenge?.liveScoring?.scoringControl;
        const exp = c?.expiresAt ? new Date(c.expiresAt).getTime() : 0;
        if (!c?.deviceId || !exp || exp <= Date.now()) return null;
        return c;
    },

    isChallengeScoringControllerOwner() {
        if (!this.isLocalChallenge()) return true;
        const owner = this.currentChallengeScoringControl();
        if (!owner) return true;
        return String(owner.deviceId || '') === String(this.scoringDeviceId || '');
    },

    canScoreNow() {
        if (!this.canUseLiveScoring() || !this.isMatchLive()) return false;
        if (this.isRemoteChallenge()) return this.isMyTurn();
        return this.isChallengeScoringControllerOwner();
    },

    canMutateLocalLiveScoring() {
        if (!this.canUseLiveScoring() || !this.isMatchLive()) return false;
        if (this.isRemoteChallenge()) return true;
        return this.isChallengeScoringControllerOwner();
    },

    scoringControlBody(extra = {}) {
        return {
            deviceId: this.scoringDeviceId,
            deviceLabel: this.scoringDeviceLabel,
            ...extra,
        };
    },

    challengeLiveKeypadVisible() {
        const finished = !!this.challenge?.liveScoring?.matchFinished;
        if (finished || !this.canUseLiveScoring()) return false;
        // Dedicated /scorer: always show the keypad shell (buttons stay disabled until canScoreNow()).
        if (this.isDedicatedChallengeSurface()) {
            if (!this.isMatchLive()) return true;
            if (this.isLocalChallenge()) return true;
            return this.isMyTurn();
        }
        if (this.isLocalChallenge()) return this.canScoreNow() || this.liveEditingVisitIndex >= 0;
        return this.isMyTurn();
    },

    challengeLiveStatsAsideVisible() {
        if (this.isDedicatedChallengeSurface()) {
            if (!this.canUseLiveScoring() || this.challenge?.liveScoring?.matchFinished) return false;
            if (!this.isMatchLive()) return false;
            if (this.isLocalChallenge()) return false;
            return !this.isMyTurn();
        }
        if (this.isLocalChallenge()) return !this.canScoreNow();
        return !this.isMyTurn();
    },

    scorerKeypadBlockedReason() {
        if (!this.isDedicatedChallengeSurface() || !this.canUseLiveScoring()) return '';
        if (this.challenge?.liveScoring?.matchFinished) return '';
        if (!this.isMatchLive()) {
            if (this.isRemoteChallenge() && this.session && String(this.session?.status || '') !== 'live') {
                return 'Session not live yet. On this console, in Challenge Session: connect cameras, then both players tap Ready.';
            }
            if (this.isRemoteChallenge() && !this.session) {
                return 'Complete Challenge Session on this console (cameras + Ready), then return to the scorer.';
            }
            if (String(this.challenge?.status || '') === 'pending') {
                if (this.isLocalChallenge()) {
                    return 'Accept the challenge here first. Local matches have no Ready or camera step.';
                }
                return 'Accept the challenge on this console first, then return to the scorer.';
            }
            if (this.isLocalChallenge()) {
                return 'Local match: no Ready step — accept the challenge if needed, then return to the scorer.';
            }
            return 'Finish pending steps on this console, then return here to score.';
        }
        if (this.isLocalChallenge() && !this.isChallengeScoringControllerOwner()) {
            return 'Keypad is locked to another device — use Take control above, or score from the device that claimed it.';
        }
        return '';
    },

    localScorerBlockedDetail() {
        const c = this.currentChallengeScoringControl();
        if (!c) return 'Open the scorer on a device or claim control from this screen.';
        return `Active scorer: ${c.userName || c.label || c.deviceId}`;
    },

    async ensureChallengeScoringClaim(force = false) {
        const admin = this.$store.session?.me?.role === 'admin';
        if (!this.isLocalChallenge() || !this.challenge?._id || (!this.isParticipant() && !admin)) return false;
        const owner = this.currentChallengeScoringControl();
        if (!force && owner && String(owner.deviceId || '') === String(this.scoringDeviceId || '')) return true;
        try {
            const res = await apiFetch(`/challenges/${this.challenge._id}/live/control/claim`, {
                method: 'POST',
                body: this.scoringControlBody({ force }),
            });
            const data = await res.json();
            if (!res.ok || !data?.ok) {
                if (data?.challenge) {
                    this.challenge = data.challenge;
                    this.scoreRows = this.buildScoreRows();
                }
                return false;
            }
            this.challenge = data.challenge || this.challenge;
            this.scoreRows = this.buildScoreRows();
            return true;
        } catch {
            return false;
        }
    },

    async takeChallengeScoringControl() {
        this.busy = true;
        try {
            const ok = await this.ensureChallengeScoringClaim(true);
            if (!ok) throw new Error('Unable to take scoring control');
            this.$store.toastr.success('Scoring control moved to this device', 2500);
            this.ensureChallengeScoringHeartbeatLoop();
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to take scoring control', 3000);
        } finally {
            this.busy = false;
        }
    },

    stopChallengeScoringHeartbeatLoop() {
        if (this.challengeScoringHeartbeatTimer) {
            window.clearInterval(this.challengeScoringHeartbeatTimer);
            this.challengeScoringHeartbeatTimer = null;
        }
    },

    ensureChallengeScoringHeartbeatLoop() {
        this.stopChallengeScoringHeartbeatLoop();
        if (!this.isLocalChallenge() || !this.challenge?._id) return;
        if (!this.isChallengeScoringControllerOwner() || !this.currentChallengeScoringControl()) return;
        this.challengeScoringHeartbeatTimer = window.setInterval(async () => {
            if (!this.isLocalChallenge() || !this.challenge?._id || !this.isChallengeScoringControllerOwner()) {
                this.stopChallengeScoringHeartbeatLoop();
                return;
            }
            try {
                await apiFetch(`/challenges/${this.challenge._id}/live/control/heartbeat`, {
                    method: 'POST',
                    body: this.scoringControlBody(),
                });
            } catch { }
        }, 10_000);
    },

    challengeReadyGuidanceVisible() {
        return !!this.challenge && !this.scoringWindow;
    },

    /** Top-column strip hidden when the same copy is shown in the Match Console sidebar above Pair TV */
    challengeReadyGuidanceMainColumnVisible() {
        if (!this.challengeReadyGuidanceVisible()) return false;
        if (this.challengePendingWaitingOnOpponent()) return false;
        if (this.challengeMatchConsoleShell() && this.isMatchLive() && this.challengeLiveScoringVisible()) {
            return false;
        }
        return true;
    },

    /** "Match in progress" guidance beside TV pair + QR on /score */
    challengeMatchConsoleAsideGuidanceVisible() {
        return this.challengeMatchConsoleShell()
            && this.challengeLiveScoringVisible()
            && this.isMatchLive()
            && !!this.challenge
            && !this.scoringWindow;
    },

    challengeReadyGuidanceTitle() {
        if (this.isMatchLive()) return 'Match in progress';
        if (this.isLocalChallenge()) {
            if (String(this.challenge?.status || '') === 'pending') return 'Waiting on the challenge';
            return 'Ready to score';
        }
        return 'Before live scoring';
    },

    challengeReadyGuidanceBody() {
        if (this.isMatchLive()) {
            return 'Enter visits on this Match Console or the mobile scorer (QR). One device should act as score controller; the TV board stays in sync.';
        }
        if (this.isLocalChallenge()) {
            if (String(this.challenge?.status || '') === 'pending') {
                return 'The opponent must accept first. Then either player can use this page or scan the QR to open the scorer on a phone.';
            }
            if (String(this.challenge?.status || '') === 'accepted') {
                return 'Both players are on the challenge — when you are ready at the board, tap Open scorer here or scan the QR. No camera session: scoring starts as soon as the match goes live.';
            }
            return 'Use Open scorer or the QR when you are ready to play.';
        }
        if (!this.$store.features.enabled('challengeSessions')) {
            return 'Remote camera sessions are off. Switch to a local challenge or enable sessions in settings.';
        }
        return 'Connect both cameras, then both players tap Ready. After the session is live, score from this Match Console or the QR device — same as a league fixture session.';
    },

    challengeReadyGuidanceToneClass() {
        if (this.isMatchLive()) {
            return 'border-emerald-200 bg-emerald-50/95 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/35 dark:text-emerald-100';
        }
        if (String(this.challenge?.status || '') === 'pending') {
            return 'border-amber-200 bg-amber-50/95 text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100';
        }
        return 'border-sky-200 bg-sky-50/90 text-sky-950 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-100';
    },

    liveVisitWillCheckout() {
        const score = Number(this.liveVisitForm.score) || 0;
        if (!score || !this.liveVisitKeypadActive()) return false;
        if (this.liveEditingVisitIndex >= 0 && Number.isFinite(Number(this.liveEditingVisitPreScore))) {
            return score === Number(this.liveEditingVisitPreScore);
        }
        const dartPlayerId = this.isLocalChallenge() ? this.currentTurnPlayerId() : this.currentPlayerId();
        return score === this.playerRemaining(dartPlayerId);
    },

    /** Aligns with fixture `fixtureScoringFrozen()` — block quick-score expand when match is done. */
    challengeLiveScoringFrozen() {
        return !!this.challenge?.liveScoring?.matchFinished;
    },

    quickPresetButtonLabel,

    visibleQuickPresets() {
        return this.quickScoreTop.slice(0, 4);
    },

    paddedVisibleQuickPresets() {
        const v = this.visibleQuickPresets();
        return [0, 1, 2, 3].map((i) => (v[i] != null ? v[i] : null));
    },

    keypadQuickTopSlotsForLiveVisit() {
        const padded = this.paddedVisibleQuickPresets();
        const editing = this.liveEditingVisitIndex >= 0;
        const dartPlayerId = this.isLocalChallenge() ? this.currentTurnPlayerId() : this.currentPlayerId();
        const remaining = this.playerRemaining(dartPlayerId);
        const active = !editing && this.canScoreNow() && !this.busy;
        return withNearCheckoutNsOnFourthQuickTop(padded, remaining, { editingVisit: editing, active });
    },

    onLiveVisitQuickTopClick(preset) {
        if (preset && typeof preset === 'object' && preset.ns) {
            void this.liveVisitNoScoreAndSubmit();
            return;
        }
        this.setLiveVisitScore(preset);
    },

    async liveVisitNoScoreAndSubmit() {
        if (!this.liveVisitKeypadActive() || this.liveEditingVisitIndex >= 0) return;
        this.setLiveVisitScore(0);
        await this.$nextTick();
        if (!this.canSubmitLiveVisit()) return;
        await this.addLiveVisit();
    },

    moreQuickPresets() {
        return mergeMoreQuickPresets(this.quickScoreMore);
    },

    hasMoreQuickPresets() {
        return this.moreQuickPresets().length > 0;
    },

    /** Mirrors fixture `scoreOutcome()` for bust banner on the keypad. */
    liveVisitScoreOutcome() {
        const raw = String(this.liveVisitForm.score || '').trim();
        if (!raw) return { valid: false, checkout: false, busted: false };
        const score = Number(raw) || 0;
        if (!Number.isFinite(score) || score < 0) return { valid: false, checkout: false, busted: false };
        const dartPlayerId = this.isLocalChallenge() ? this.currentTurnPlayerId() : this.currentPlayerId();
        const remaining = this.liveEditingVisitIndex >= 0 && Number.isFinite(Number(this.liveEditingVisitPreScore))
            ? Number(this.liveEditingVisitPreScore)
            : this.playerRemaining(dartPlayerId);
        const nextRemaining = Math.max(0, remaining - score);
        const busted = score > remaining || nextRemaining === 1;
        const checkout = !busted && nextRemaining === 0;
        return { valid: true, checkout, busted, remaining: nextRemaining };
    },

    liveVisitKeypadActive() {
        return this.canScoreNow() || (this.liveEditingVisitIndex >= 0 && this.canMutateLocalLiveScoring());
    },

    canSubmitLiveVisit() {
        if (this.busy || !this.liveVisitKeypadActive()) return false;
        if (!String(this.liveVisitForm.score || '').trim()) return false;
        if (this.liveVisitWillCheckout() && !Number(this.liveVisitForm.checkoutDarts)) return false;
        return true;
    },

    editLiveVisit(visit, index) {
        if (!this.canMutateLocalLiveScoring() || !this.isMatchLive()) return;
        this.liveVisitForm.score = String(Number(visit?.score || 0));
        this.liveVisitForm.checkoutDarts = '';
        this.liveVisitForm.checkoutLabel = String(visit?.checkoutLabel || '').trim() || '';
        this.liveEditingVisitIndex = Number(index);
        this.liveEditingVisitPreScore = Number.isFinite(Number(visit?.preScore)) ? Number(visit.preScore) : null;
        window.requestAnimationFrame(() => {
            try {
                (this.$refs?.liveScoreInputDesktop || this.$refs?.liveScoreInputDesktopMc)?.focus?.();
            } catch { /* ignore */ }
        });
    },

    cancelLiveVisitEdit() {
        this.liveEditingVisitIndex = -1;
        this.liveEditingVisitPreScore = null;
        this.liveVisitForm = { score: '', checkoutDarts: '', checkoutLabel: '' };
    },

    isEditingLiveHistoryEntry(index) {
        return Number(this.liveEditingVisitIndex) === Number(index);
    },

    async saveLiveVisitEdit() {
        if (!this.canMutateLocalLiveScoring() || this.liveEditingVisitIndex < 0) return;
        this.busy = true;
        try {
            const score = Number(this.liveVisitForm.score) || 0;
            const res = await apiFetch(`/challenges/${this.challenge._id}/live/visits/${this.liveEditingVisitIndex}`, {
                method: 'PATCH',
                body: this.isLocalChallenge()
                    ? { score, ...this.scoringControlBody() }
                    : { score },
            });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to update throw');
            this.challenge = data.challenge || this.challenge;
            this.scoreRows = this.buildScoreRows();
            this.cancelLiveVisitEdit();
            this.$store.toastr.success('Throw updated', 2500);
            this.ensureChallengeScoringHeartbeatLoop();
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to update throw', 3000);
        } finally {
            this.busy = false;
        }
    },

    liveScoreEntryTitle() {
        if (!this.isLocalChallenge() || !this.canScoreNow() || this.isMyTurn()) return 'Enter Score';
        return `Enter score for ${this.playerLabel(this.liveStagePrimaryPlayer())}`;
    },

    liveStagePrimaryPlayer() {
        if (this.isMyTurn()) return this.challenge?.challengerPlayerId?._id === this.currentPlayerId()
            ? this.challenge?.challengerPlayerId
            : this.challenge?.challengedPlayerId;
        return this.currentTurnPlayerId() === String(this.challenge?.challengerPlayerId?._id || this.challenge?.challengerPlayerId || '')
            ? this.challenge?.challengerPlayerId
            : this.challenge?.challengedPlayerId;
    },

    liveStageSecondaryPlayer() {
        const primaryId = String(this.liveStagePrimaryPlayer()?._id || this.liveStagePrimaryPlayer() || '');
        const challengerId = String(this.challenge?.challengerPlayerId?._id || this.challenge?.challengerPlayerId || '');
        return primaryId === challengerId ? this.challenge?.challengedPlayerId : this.challenge?.challengerPlayerId;
    },

    recentLiveVisits(limit = 4) {
        return this.liveLegs()
            .flatMap((leg) => leg?.visits || [])
            .slice(-limit)
            .reverse();
    },

    stageShouldShowCamera() {
        return this.hasMediaSession() && ((this.isMyTurn() && this.hasLocalPreview()) || (!this.isMyTurn() && this.hasRemotePreview()));
    },

    effectiveLiveFocusMode() {
        return this.liveFocusMode;
    },

    syncFocusState() {
        this.syncFocusClass();
    },

    syncFocusClass() {
        const shouldLock = !!this.challenge && this.isMatchLive() && this.effectiveLiveFocusMode();
        document.body.classList.toggle('overflow-hidden', shouldLock);
    },

    stageVideoRef() {
        return this.isMyTurn() ? 'stageLocalVideo' : 'stageRemoteVideo';
    },

    stageVideoPlayerLabel() {
        return this.isMyTurn() ? this.playerLabel(this.liveStagePrimaryPlayer()) : this.playerLabel(this.liveStagePrimaryPlayer());
    },

    stageStatusText() {
        if (this.stageShouldShowCamera()) return 'Your turn. Stay on the board and enter your visit.';
        if (this.currentTurnPlayerId()) return `${this.playerLabel(this.liveStagePrimaryPlayer())} is throwing`;
        return 'Waiting for the next visit';
    },

    stageStreamLabel() {
        if (this.isMyTurn()) return '';
        if (this.hasRemotePreview()) return 'Opponent Camera';
        return 'Match Snapshot';
    },

    canStartNextLeg() {
        const legs = this.liveLegs();
        if (!legs.length) return false;
        const last = legs[legs.length - 1];
        return this.canMutateLocalLiveScoring() && !!last?.complete && !this.liveScoring()?.matchFinished;
    },

    canRemoveChallenge() {
        const me = String(this.$store.session?.me?._id || '');
        const ownerId = String(this.challenge?.createdByPlayerId || '');
        const isAdminRole = this.$store.session?.me?.role === 'admin';
        return (isAdminRole || ownerId === me) && ['pending', 'declined', 'cancelled'].includes(String(this.challenge?.status || ''));
    },

    async removeChallenge() {
        if (!this.canRemoveChallenge()) return;
        if (!window.confirm('Remove this challenge? This will delete the challenge and its session.')) return;
        this.busy = true;
        try {
            const res = await apiFetch(`/challenges/${this.challenge._id}/remove`, { method: 'POST', body: {} });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to remove challenge');
            this.$store.toastr.success('Challenge removed', 3000);
            window.location.href = '/admin/challenges';
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to remove challenge', 3000);
        } finally {
            this.busy = false;
        }
    },

    playerLiveStats(playerRef) {
        const playerId = String(playerRef?._id || playerRef || '');
        const allVisits = this.liveLegs().flatMap((leg) => (leg?.visits || []).filter((visit) => String(visit.playerId?._id || visit.playerId) === playerId));
        const points = allVisits.filter((visit) => !visit.bust).reduce((sum, visit) => sum + (Number(visit.score) || 0), 0);
        const darts = allVisits.reduce((sum, visit) => sum + (Number(visit.darts) || 0), 0);
        const firstNineVisits = allVisits.slice(0, 3);
        const firstNinePoints = firstNineVisits.filter((visit) => !visit.bust).reduce((sum, visit) => sum + (Number(visit.score) || 0), 0);
        const firstNineDarts = firstNineVisits.reduce((sum, visit) => sum + (Number(visit.darts) || 0), 0);
        return {
            average: darts ? ((points / darts) * 3) : 0,
            firstNineAverage: firstNineDarts ? ((firstNinePoints / firstNineDarts) * 3) : 0,
            points,
            darts,
        };
    },

    checkoutSuggestionsFor(playerRef) {
        const remaining = this.playerRemaining(playerRef?._id || playerRef);
        const rule = String(this.challenge?.format?.checkout || 'double-out');
        if (rule === 'straight-out') return remaining <= 170 ? [`Take ${remaining}`] : [];
        return OUT_SUGGESTIONS[remaining] || [];
    },

    visitBadgeClasses(visit) {
        if (visit?.checkout) return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
        if (visit?.bust) return 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200';
        return 'bg-slate-100 text-slate-700 dark:bg-neutral-800 dark:text-neutral-200';
    },

    visitBadgeText(visit) {
        if (visit?.checkout) return visit?.checkoutLabel ? `Checkout ${visit.checkoutLabel}` : 'Checkout';
        if (visit?.bust) return 'Bust';
        return `${visit?.darts || 3} darts`;
    },

    setLiveVisitScore(value) {
        if (!this.liveVisitKeypadActive()) return;
        if (value && typeof value === 'object') return;
        const numeric = Math.min(180, Number(value || 0));
        this.liveVisitForm.score = String(Number.isFinite(numeric) ? numeric : '');
        this.liveVisitForm.checkoutDarts = '';
    },

    appendLiveVisitDigit(digit) {
        if (!this.liveVisitKeypadActive()) return;
        const current = String(this.liveVisitForm.score || '');
        const next = `${current}${digit}`.replace(/^0+(?=\d)/, '');
        const parsed = Number(next);
        if (!Number.isFinite(parsed)) return;
        this.liveVisitForm.score = String(Math.max(0, Math.min(180, parsed)));
        this.liveVisitForm.checkoutDarts = '';
    },

    backspaceLiveVisitScore() {
        if (!this.liveVisitKeypadActive()) return;
        const current = String(this.liveVisitForm.score || '');
        this.liveVisitForm.score = current.length > 1 ? current.slice(0, -1) : '';
        this.liveVisitForm.checkoutDarts = '';
    },

    clearLiveVisitScore() {
        if (!this.liveVisitKeypadActive()) return;
        this.liveVisitForm.score = '';
        this.liveVisitForm.checkoutDarts = '';
    },

    toggleLiveFocusMode() {
        this.liveFocusMode = !this.liveFocusMode;
        this.syncFocusClass();
        this.attachStreams();
    },

    legStarterName(leg) {
        const starterId = String(leg?.startingPlayerId || '');
        if (starterId === String(this.challenge?.challengerPlayerId?._id || this.challenge?.challengerPlayerId || '')) return this.playerLabel(this.challenge?.challengerPlayerId);
        if (starterId === String(this.challenge?.challengedPlayerId?._id || this.challenge?.challengedPlayerId || '')) return this.playerLabel(this.challenge?.challengedPlayerId);
        return 'Starter';
    },

    sessionEventUrl() {
        const token = encodeURIComponent(getAccessToken() || '');
        const apiBase = String(window.__API_BASE__ || '').replace(/\/+$/, '');
        const path = `/api/challenges/${this.challenge?._id}/session/events?access_token=${token}`;
        return apiBase ? `${apiBase}${path}` : path;
    },

    ensureEventStream() {
        if (!this.session?.runtime?.hasEventStream || this.eventSource || !this.challenge?._id || !window.EventSource) {
            if (!this.eventSource && this.isRemoteChallenge() && this.session) this.ensureSignalingLoop();
            return;
        }

        try {
            this.eventSource = new EventSource(this.sessionEventUrl());
            this.eventSource.addEventListener('session:update', async (event) => {
                const payload = JSON.parse(event.data || '{}');
                this.eventStreamHealthy = true;
                if (payload?.session) {
                    const previousSession = this.session;
                    this.session = payload.session;
                    this.seedFallbackForm();
                    this.syncSessionUiState();
                    this.evaluateSessionInterruption(previousSession, this.session, 'session:update');
                    if (this.isRemoteChallenge()) await this.syncPeerFromSession();
                }
            });
            for (const evt of ['session:created', 'session:presence', 'session:signal', 'session:ready', 'session:live', 'session:ended', 'session:fallback', 'session:diagnostics', 'session:health', 'session:heartbeat']) {
                this.eventSource.addEventListener(evt, async (event) => {
                    const payload = JSON.parse(event.data || '{}');
                    this.eventStreamHealthy = true;
                    if (payload?.session) {
                        const previousSession = this.session;
                        this.session = payload.session;
                        this.seedFallbackForm();
                        this.syncSessionUiState();
                        this.evaluateSessionInterruption(previousSession, this.session, evt);
                        if (this.isRemoteChallenge()) await this.syncPeerFromSession();
                    }
                });
            }
            this.eventSource.onerror = () => {
                this.eventStreamHealthy = false;
                this.ensureSignalingLoop();
            };
            this.stopSignalingLoop();
        } catch {
            this.eventStreamHealthy = false;
            this.ensureSignalingLoop();
        }
    },

    disconnectEventStream() {
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }
        this.eventStreamHealthy = false;
    },

    ensureHeartbeatLoop() {
        if (this.heartbeatTimer || !this.challenge?._id) return;
        this.heartbeatTimer = window.setInterval(async () => {
            if (!this.session || !this.mySessionParticipant()?.connected) return;
            try {
                await apiFetch(`/challenges/${this.challenge._id}/session/heartbeat`, { method: 'POST', body: {} });
                if (this.isMatchLive() && !this.challengeStreamHealthy) {
                    await this.refreshChallenge();
                }
            } catch { }
        }, 10_000);
        this.ensureChallengePollingLoop();
    },

    stopHeartbeatLoop() {
        if (this.heartbeatTimer) {
            window.clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    },

    canCaptureScoreKeyboard(event) {
        if (!this.canUseLiveScoring() || !this.isMatchLive()) return false;
        if (event.altKey || event.ctrlKey || event.metaKey) return false;
        const active = document.activeElement;
        const tag = String(active?.tagName || '').toLowerCase();
        if (tag === 'textarea' || tag === 'select') return false;
        if (tag === 'input' && active !== this.$refs?.checkoutLabelInput && active !== this.$refs?.liveScoreInput
            && active !== this.$refs?.liveScoreInputDesktop && active !== this.$refs?.liveScoreInputDesktopMc) return false;
        return true;
    },

    onLiveScoreKeydown(event) {
        if (!this.canCaptureScoreKeyboard(event)) return;
        const key = String(event.key || '');
        if (/^\d$/.test(key) && this.liveVisitKeypadActive()) {
            event.preventDefault();
            this.appendLiveVisitDigit(Number(key));
            return;
        }
        if ((key === 'Backspace' || key === 'Delete') && this.liveVisitKeypadActive()) {
            event.preventDefault();
            if (key === 'Backspace') this.backspaceLiveVisitScore();
            else this.clearLiveVisitScore();
            return;
        }
        if (key === 'Escape' && this.liveEditingVisitIndex >= 0) {
            event.preventDefault();
            this.cancelLiveVisitEdit();
            return;
        }
        if ((key === 'Enter' || key === 'NumpadEnter') && this.canSubmitLiveVisit()) {
            event.preventDefault();
            if (this.liveEditingVisitIndex >= 0) this.saveLiveVisitEdit();
            else this.addLiveVisit();
            return;
        }
        if (this.liveVisitWillCheckout() && ['1', '2', '3'].includes(key)) {
            event.preventDefault();
            this.liveVisitForm.checkoutDarts = key;
        }
    },

    async pushDiagnostics(payload = {}) {
        if (!this.session || !this.challenge?._id) return;
        try {
            await apiFetch(`/challenges/${this.challenge._id}/session/diagnostics`, { method: 'POST', body: payload });
        } catch { }
    },

    async runHealthCheck() {
        this.diagnosticsBusy = true;
        try {
            await this.sessionAction(`/challenges/${this.challenge._id}/session/health`, {}, 'Venue camera health checked');
        } finally {
            this.diagnosticsBusy = false;
        }
    },

    async addLiveVisit() {
        if (!this.canUseLiveScoring()) return;
        if (this.liveEditingVisitIndex >= 0) {
            await this.saveLiveVisitEdit();
            return;
        }
        this.busy = true;
        try {
            const visitBody = {
                score: Number(this.liveVisitForm.score) || 0,
                checkoutDarts: this.liveVisitWillCheckout() ? (Number(this.liveVisitForm.checkoutDarts) || 3) : null,
                checkoutLabel: this.liveVisitForm.checkoutLabel,
            };
            const res = await apiFetch(`/challenges/${this.challenge._id}/live/visit`, {
                method: 'POST',
                body: this.isLocalChallenge() ? { ...visitBody, ...this.scoringControlBody() } : visitBody,
            });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to add visit');
            this.challenge = data.challenge || this.challenge;
            this.scoreRows = this.buildScoreRows();
            this.liveVisitForm = { score: '', checkoutDarts: '', checkoutLabel: '' };
            this.ensureChallengeScoringHeartbeatLoop();
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to add visit', 3000);
        } finally {
            this.busy = false;
        }
    },

    async undoLiveVisit() {
        this.busy = true;
        try {
            const res = await apiFetch(`/challenges/${this.challenge._id}/live/undo`, {
                method: 'POST',
                body: this.isLocalChallenge() ? this.scoringControlBody() : {},
            });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to undo visit');
            this.challenge = data.challenge || this.challenge;
            this.scoreRows = this.buildScoreRows();
            this.ensureChallengeScoringHeartbeatLoop();
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to undo visit', 3000);
        } finally {
            this.busy = false;
        }
    },

    async startNextLeg() {
        this.busy = true;
        try {
            const res = await apiFetch(`/challenges/${this.challenge._id}/live/next-leg`, {
                method: 'POST',
                body: this.isLocalChallenge() ? this.scoringControlBody() : {},
            });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to start next leg');
            this.challenge = data.challenge || this.challenge;
            this.scoreRows = this.buildScoreRows();
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to start next leg', 3000);
        } finally {
            this.busy = false;
        }
    },

    canEndLiveSession() {
        return this.isParticipant() && this.session && String(this.session?.status || '') === 'live';
    },

    async createSession() {
        await this.ensureSessionExists(false);
    },

    async connectSession() {
        try {
            const source = await resolveKioskCameraSource(this);
            if (source === 'tablet') {
                const blocked = mediaDevicesBlockedReason();
                if (blocked) {
                    this.$store.toastr.error(blocked, 7000);
                    return;
                }
            } else {
                const blocked = mediaDevicesBlockedReason();
                if (blocked) {
                    this.$store.toastr.error(blocked, 7000);
                    return;
                }
                const status = await fetchVenueCameraHostStatus({ force: true });
                if (!status?.hostOnline) {
                    this.$store.toastr.error('Venue PC camera is offline — open Venue Control on the venue PC or use the tablet camera.', 7000);
                    return;
                }
            }
            const shouldResetSignaling = this.canReconnectSession() && !!this.mySessionParticipant()?.connected;
            if (this.canResumeSession()) {
                await this.ensureSessionExists(true);
            }
            await this.openMedia();
            await this.registerSessionConnect(shouldResetSignaling);
            await this.syncPeerFromSession();
        } catch (e) {
            this.$store.toastr.error(e?.message || 'Unable to reconnect camera for this challenge session', 3000);
        }
    },

    shouldAutoStartLocalCamera() {
        const status = String(this.challenge?.status || '');
        if (['pending', 'declined', 'cancelled'].includes(status)) return false;
        if (this.hasLocalPreview() || this.mediaBusy) return false;
        return this.isRemoteChallenge()
            && this.$store.features.enabled('challengeSessions')
            && this.isParticipant()
            && !!this.session;
    },

    canPromptForCamera() {
        if (!this.isRemoteChallenge() || !this.$store.features.enabled('challengeSessions')) return false;
        if (!this.isParticipant() || !this.session || this.hasLocalPreview() || this.mediaBusy) return false;
        return canUseBrowserMediaDevices();
    },

    cameraPromptBlockedReason() {
        return mediaDevicesBlockedReason();
    },

    stopLobbyCameraAutoStart() {
        if (this._lobbyCameraAutoStartTimer) {
            window.clearTimeout(this._lobbyCameraAutoStartTimer);
            this._lobbyCameraAutoStartTimer = null;
        }
    },

    /** Lobby embed: ensure session exists, then auto-open camera with retries. */
    scheduleLobbyCameraAutoStart(attempt = 0) {
        if (!this.challengeLobbyShell() || !this.isVenueKioskEmbed() || !this.isRemoteChallenge()) return;
        if (this.hasLocalPreview() || this._localCameraPermissionDenied) return;
        const maxAttempts = 8;
        if (attempt >= maxAttempts) return;
        this._lobbyCameraAutoStartTimer = window.setTimeout(async () => {
            this._lobbyCameraAutoStartTimer = null;
            if (!this.challengeLobbyShell() || this.hasLocalPreview() || this._localCameraPermissionDenied) return;
            try {
                if (!this.session && this.isParticipant()) {
                    await this.ensureSessionExists(true);
                }
                if (this.session) {
                    await this.maybeAutoStartLocalCameraPreview({ force: true });
                }
            } catch {
                /* retry below */
            }
            if (!this.hasLocalPreview() && !this._localCameraPermissionDenied) {
                this.scheduleLobbyCameraAutoStart(attempt + 1);
            }
        }, attempt === 0 ? 80 : 450 + attempt * 350);
    },

    async maybeAutoStartLocalCameraPreview(opts = {}) {
        const force = !!opts.force;
        if (this._localCameraPermissionDenied) return;
        if (!force && (!this.shouldAutoStartLocalCamera() || this._localCameraAutoStartAttempted)) return;
        if (!this.shouldAutoStartLocalCamera() && !force) return;
        const source = await resolveKioskCameraSource(this);
        if (source === 'tablet') {
            const blocked = mediaDevicesBlockedReason();
            if (blocked) {
                this.mediaStatusText = blocked;
                this.$store.toastr.error(blocked, 7000);
                return;
            }
        } else {
            const blocked = mediaDevicesBlockedReason();
            if (blocked) {
                this.mediaStatusText = blocked;
                this.$store.toastr.error(blocked, 7000);
                return;
            }
            await this.refreshVenueCameraHostStatus();
            if (!this.venuePcCameraAvailable()) {
                this.mediaStatusText = 'Venue PC camera offline — switch to tablet camera or start Venue Control.';
                if (force) this.$store.toastr.error(this.mediaStatusText, 6000);
                return;
            }
        }
        this._localCameraAutoStartAttempted = true;
        try {
            await this.openMedia();
            if (this.canConnectSession()) {
                await this.registerSessionConnect(false);
            }
            await this.syncPeerFromSession();
        } catch (e) {
            const denied = e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError';
            if (denied) {
                this._localCameraPermissionDenied = true;
            } else if (!force) {
                this._localCameraAutoStartAttempted = false;
            }
            let msg = e?.message || mediaDevicesBlockedReason() || 'Unable to access camera or microphone';
            if (denied && this.isVenueKioskEmbed()) {
                if (this.kioskCameraSource === 'venue-pc') {
                    msg = 'Microphone permission denied. Venue PC camera video does not need the tablet camera — allow Microphone in DartsOS Kiosk app settings if you want voice chat, then tap Refresh.';
                } else {
                    msg = 'Camera permission denied. On the DartsOS Kiosk app, allow Camera and Microphone when Android prompts you (or in Settings > Apps > DartsOS Kiosk > Permissions), then tap Refresh on the lobby.';
                }
            }
            if (!denied || force) this.$store.toastr.error(msg, 7000);
        }
    },

    async registerSessionConnect(shouldResetSignaling = false) {
        this.resetPeerConnection({ keepMedia: true });
        await this.pushDiagnostics({ connectionState: 'connecting', iceConnectionState: 'checking', failureReason: '' });
        await this.sessionAction(
            `/challenges/${this.challenge._id}/session/connect`,
            { cameraEnabled: true, micEnabled: !this.micMuted, resetSignaling: shouldResetSignaling },
            'Camera and microphone connected',
        );
    },

    async heartbeatSession() {
        await this.sessionAction(`/challenges/${this.challenge._id}/session/heartbeat`, {}, '');
    },

    async markSessionReady() {
        if (this.canResumeSession()) {
            await this.ensureSessionExists(false);
            this.sessionSetupOpen = true;
            this.$nextTick(() => {
                this.$refs?.sessionSetup?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
            });
            return;
        }
        const successText = this.otherSessionParticipant()?.readyAt ? 'Both players ready. Match is live.' : 'Ready. Waiting for the other player.';
        await this.sessionAction(`/challenges/${this.challenge._id}/session/ready`, { ready: true }, successText);
    },

    async endLiveSession() {
        this.suppressNextSessionEndedDialog = true;
        await this.sessionAction(`/challenges/${this.challenge._id}/session/end`, {}, 'Live session ended');
    },

    async quitLiveMatch() {
        if (!this.isMatchLive()) {
            window.location.href = '/admin/challenges';
            return;
        }
        if (!window.confirm('Quit this live match? This will end the session for both players.')) return;
        await this.endLiveSession();
        window.location.href = '/admin/challenges';
    },

    async saveFallback() {
        await this.sessionAction(`/challenges/${this.challenge._id}/session/fallback`, { ...this.fallbackForm }, 'Venue camera fallback updated');
        this.seedFallbackForm();
    },

    async sessionAction(url, body, successText) {
        this.busy = true;
        try {
            const res = await apiFetch(url, { method: 'POST', body });
            let data = null;
            try {
                data = await res.json();
            } catch {
                data = null;
            }
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Session action failed');
            this.session = data.session || this.session;
            this.seedFallbackForm();
            this.syncSessionUiState();
            if (String(this.session?.status || '') === 'live') {
                await this.refreshChallenge();
            }
            // if (successText) this.$store.toastr.success(successText, 3000);
            if (this.isRemoteChallenge() && this.session) {
                this.ensureEventStream();
                await this.syncPeerFromSession();
            }
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to update challenge session', 3000);
        } finally {
            this.busy = false;
        }
    },

    async openMedia(opts = {}) {
        const strict = opts.source != null || opts.force === true;
        const requested = strict
            ? normalizeKioskCameraSource(opts.source ?? this.kioskCameraSource)
            : null;
        if (requested) this.kioskCameraSource = requested;
        let source;
        try {
            source = await resolveKioskCameraSource(
                strict
                    ? { ...this, kioskCameraSource: requested, explicitKioskCameraSource: true }
                    : this,
            );
        } catch (e) {
            this.mediaStatusText = e?.message || 'Camera unavailable';
            throw e;
        }
        const withAudio = opts.withAudio !== undefined
            ? opts.withAudio !== false
            : !(this.challengeLobbyShell() && !this.isMatchLive());
        const blocked = (withAudio || source !== 'venue-pc')
            ? mediaDevicesBlockedReason()
            : (globalThis.isSecureContext ? '' : mediaDevicesBlockedReason());
        if (blocked) {
            this.mediaStatusText = blocked;
            throw new Error(blocked);
        }
        if (!opts.force && localStreamReadyForSource(this, source)) {
            this.attachStreams();
            this.syncAudioState();
            return;
        }
        if (this.localStream) {
            for (const track of this.localStream.getTracks()) track.stop();
            this.localStream = null;
        }
        await closeVenuePcBridge(this);
        this.mediaBusy = true;
        try {
            const used = await openKioskMedia(this, { source, withAudio });
            this.kioskCameraSource = used;
            persistKioskCameraSource(used);
            if (!(this.localStream?.getAudioTracks?.()?.length)) {
                this.micMuted = true;
            }
            this.syncAudioState();
            this.startLocalAudioMeter();
            this.mediaStatusText = used === 'venue-pc'
                ? (this.localStream?.getAudioTracks?.()?.length
                    ? 'Venue PC camera connected'
                    : 'Venue PC camera connected — tap Unmute Mic for voice chat')
                : 'Camera and microphone connected';
            this.attachStreams();
        } catch (e) {
            this.mediaStatusText = 'Camera or microphone access failed';
            this.$store.toastr.error(e?.message || 'Unable to access camera or microphone for this challenge session', 3000);
            throw e;
        } finally {
            this.mediaBusy = false;
        }
    },

    syncAudioState() {
        for (const track of this.localStream?.getAudioTracks?.() || []) {
            track.enabled = !this.micMuted;
        }
    },

    stopLocalAudioMeter() {
        if (this.audioMeterFrame) {
            window.cancelAnimationFrame(this.audioMeterFrame);
            this.audioMeterFrame = null;
        }
        try { this.audioSourceNode?.disconnect?.(); } catch { }
        this.audioSourceNode = null;
        try { this.audioAnalyser?.disconnect?.(); } catch { }
        this.audioAnalyser = null;
        try { this.audioContext?.close?.(); } catch { }
        this.audioContext = null;
        this.localMicLevel = 0;
    },

    startLocalAudioMeter() {
        this.stopLocalAudioMeter();
        if (!this.hasLiveMicrophone() || !window.AudioContext) return;
        try {
            this.audioContext = new window.AudioContext();
            this.audioAnalyser = this.audioContext.createAnalyser();
            this.audioAnalyser.fftSize = 256;
            this.audioSourceNode = this.audioContext.createMediaStreamSource(this.localStream);
            this.audioSourceNode.connect(this.audioAnalyser);
            const samples = new Uint8Array(this.audioAnalyser.frequencyBinCount);
            const tick = () => {
                if (!this.audioAnalyser) return;
                this.audioAnalyser.getByteFrequencyData(samples);
                const average = samples.reduce((sum, value) => sum + value, 0) / (samples.length || 1);
                this.localMicLevel = this.micMuted ? 0 : Math.max(0, Math.min(1, average / 120));
                this.audioMeterFrame = window.requestAnimationFrame(tick);
            };
            tick();
        } catch {
            this.localMicLevel = 0;
        }
    },

    stopRemoteAudioMeter() {
        if (this.remoteAudioMeterFrame) {
            window.cancelAnimationFrame(this.remoteAudioMeterFrame);
            this.remoteAudioMeterFrame = null;
        }
        try { this.remoteAudioSourceNode?.disconnect?.(); } catch { }
        this.remoteAudioSourceNode = null;
        try { this.remoteAudioAnalyser?.disconnect?.(); } catch { }
        this.remoteAudioAnalyser = null;
        try { this.remoteAudioContext?.close?.(); } catch { }
        this.remoteAudioContext = null;
        this.remoteMicLevel = 0;
    },

    startRemoteAudioMeter() {
        this.stopRemoteAudioMeter();
        if (!this.hasRemoteMicrophone() || !window.AudioContext) return;
        try {
            this.remoteAudioContext = new window.AudioContext();
            this.remoteAudioAnalyser = this.remoteAudioContext.createAnalyser();
            this.remoteAudioAnalyser.fftSize = 256;
            this.remoteAudioSourceNode = this.remoteAudioContext.createMediaStreamSource(this.remoteStream);
            this.remoteAudioSourceNode.connect(this.remoteAudioAnalyser);
            const samples = new Uint8Array(this.remoteAudioAnalyser.frequencyBinCount);
            const tick = () => {
                if (!this.remoteAudioAnalyser) return;
                this.remoteAudioAnalyser.getByteFrequencyData(samples);
                const average = samples.reduce((sum, value) => sum + value, 0) / (samples.length || 1);
                this.remoteMicLevel = Math.max(0, Math.min(1, average / 120));
                this.remoteAudioMeterFrame = window.requestAnimationFrame(tick);
            };
            tick();
        } catch {
            this.remoteMicLevel = 0;
        }
    },

    async toggleMicMute() {
        if (this.micMuted) {
            try {
                const ok = await ensureKioskMicTrack(this, this.localStream, { required: false });
                if (!ok) {
                    this.$store.toastr.error(
                        'Microphone permission denied. Allow Microphone in DartsOS Kiosk app settings, then try again.',
                        5000,
                    );
                    return;
                }
                if (this.peer) {
                    this.syncLocalTracks(this.peer);
                }
            } catch (e) {
                this.$store.toastr.error(e?.message || 'Microphone unavailable', 5000);
                return;
            }
        }
        this.micMuted = !this.micMuted;
        this.syncAudioState();
        if (this.session && this.mySessionParticipant()?.connected) {
            try {
                const res = await apiFetch(`/challenges/${this.challenge._id}/session/connect`, {
                    method: 'POST',
                    body: { cameraEnabled: true, micEnabled: !this.micMuted },
                });
                const data = await res.json();
                if (res.ok && data?.ok) {
                    this.session = data.session || this.session;
                }
            } catch { }
        }
    },

    syncLocalTracks(pc) {
        if (!pc || !this.localStream) return;
        const senders = pc.getSenders?.() || [];
        for (const track of this.localStream.getTracks()) {
            const sender = senders.find((row) => row.track?.kind === track.kind);
            if (sender) {
                if (sender.track?.id !== track.id) {
                    void sender.replaceTrack(track);
                }
                continue;
            }
            pc.addTrack(track, this.localStream);
        }
        for (const sender of senders) {
            const kind = sender.track?.kind;
            if (!kind) continue;
            const stillPresent = this.localStream.getTracks().some((track) => track.kind === kind);
            if (!stillPresent) void sender.replaceTrack(null);
        }
    },

    attachVideoStream(videoEl, stream) {
        if (!videoEl) return;
        videoEl.srcObject = stream || null;
        if (!stream) return;
        const playAttempt = videoEl.play?.();
        if (playAttempt && typeof playAttempt.catch === 'function') {
            playAttempt.catch(() => {});
        }
    },

    attachStreams() {
        this.$nextTick(() => {
            this.attachVideoStream(this.$refs.localVideo, this.localStream);
            this.attachVideoStream(this.$refs.stageLocalVideo, this.localStream);
            this.attachVideoStream(this.$refs.remoteVideo, this.remoteStream);
            this.attachVideoStream(this.$refs.stageRemoteVideo, this.remoteStream);
            this.attachVideoStream(this.$refs.remoteAudio, this.remoteStream);
        });
    },

    async ensurePeerConnection() {
        if (this.peer) {
            this.syncLocalTracks(this.peer);
            return this.peer;
        }
        const pc = new RTCPeerConnection({
            iceServers: this.session?.runtime?.iceServers || [
                { urls: 'stun:stun.l.google.com:19302' },
                { urls: 'stun:stun1.l.google.com:19302' },
            ],
        });
        this.peer = pc;
        this.remoteStream = new MediaStream();
        this.attachStreams();
        this.syncLocalTracks(pc);

        pc.ontrack = (event) => {
            const incomingTracks = event.streams?.[0]?.getTracks?.() || (event.track ? [event.track] : []);
            const existingIds = new Set((this.remoteStream?.getTracks?.() || []).map((track) => track.id));
            for (const track of incomingTracks) {
                if (!track || existingIds.has(track.id)) continue;
                this.remoteStream.addTrack(track);
            }
            if (this.hasRemoteMicrophone()) {
                this.startRemoteAudioMeter();
            }
            this.webrtcReady = this.hasRemotePreview() || this.webrtcReady;
            if (this.hasRemotePreview()) {
                this.mediaStatusText = 'Peer connected';
                this.pushDiagnostics({
                    connectionState: 'connected',
                    iceConnectionState: 'connected',
                    relayUsed: String(pc.iceConnectionState || '').toLowerCase().includes('relay'),
                    failureReason: '',
                });
            }
            this.attachStreams();
        };

        pc.onicecandidate = async (event) => {
            if (!event.candidate || !this.session) return;
            await this.sendSignal({ candidate: event.candidate.toJSON() });
        };

        pc.onconnectionstatechange = () => {
            this.webrtcReady = ['connected', 'completed'].includes(String(pc.connectionState || ''));
            this.mediaStatusText = this.webrtcReady ? 'Peer connected' : (pc.connectionState === 'failed' ? 'Peer connection failed. Reconnect or use a TURN relay.' : `Connection ${String(pc.connectionState || 'new')}`);
            this.pushDiagnostics({
                connectionState: String(pc.connectionState || ''),
                relayUsed: String(pc.iceConnectionState || '').toLowerCase().includes('relay'),
                failureReason: pc.connectionState === 'failed' ? 'Peer connection failed' : '',
            });
            if (pc.connectionState === 'failed') {
                this.attemptIceRestart(pc).then((restarted) => {
                    if (!restarted) this.schedulePeerFailureToast(pc);
                }).catch(() => {
                    this.schedulePeerFailureToast(pc);
                });
            }
            if (this.webrtcReady) {
                this.iceRestartAttempts = 0;
                if (this.peerFailureToastTimer) {
                    window.clearTimeout(this.peerFailureToastTimer);
                    this.peerFailureToastTimer = null;
                }
            }
        };

        pc.oniceconnectionstatechange = () => {
            this.pushDiagnostics({
                iceConnectionState: String(pc.iceConnectionState || ''),
                relayUsed: String(pc.iceConnectionState || '').toLowerCase().includes('relay'),
                failureReason: pc.iceConnectionState === 'failed' ? 'ICE connectivity failed' : '',
            });
        };

        return pc;
    },

    resetPeerConnection({ keepMedia = false } = {}) {
        if (this.peer) {
            try { this.peer.close(); } catch { }
            this.peer = null;
        }
        this.remoteStream = new MediaStream();
        this.webrtcReady = false;
        this.remoteDescriptionApplied = false;
        this.answerSent = false;
        this.appliedOfferSdp = '';
        this.appliedAnswerSdp = '';
        this.iceRestartAttempts = 0;
        this.iceRestartInFlight = false;
        if (this.peerFailureToastTimer) {
            window.clearTimeout(this.peerFailureToastTimer);
            this.peerFailureToastTimer = null;
        }
        this.seenOfferCandidates.clear();
        this.seenAnswerCandidates.clear();
        this.signalingGeneration = Number(this.session?.signaling?.generation ?? this.signalingGeneration ?? -1);
        if (!keepMedia && this.localStream) {
            void closeVenuePcBridge(this);
            for (const track of this.localStream.getTracks()) track.stop();
            this.localStream = null;
        }
        if (!keepMedia) this.stopLocalAudioMeter();
        this.stopRemoteAudioMeter();
        this.attachStreams();
    },

    isOfferer() {
        return String(this.mySessionParticipant()?.role || '') === 'challenger';
    },

    async sendSignal(payload) {
        const res = await apiFetch(`/challenges/${this.challenge._id}/session/signal`, { method: 'POST', body: payload });
        const data = await res.json();
        if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to send signal');
        this.session = data.session || this.session;
    },

    async attemptIceRestart(pc = this.peer) {
        if (!pc || !this.isOfferer() || !this.hasLocalPreview() || this.iceRestartInFlight) return false;
        if (this.iceRestartAttempts >= 1) return false;
        this.iceRestartInFlight = true;
        this.iceRestartAttempts += 1;
        try {
            this.mediaStatusText = 'Retrying peer connection';
            const offer = await pc.createOffer({ iceRestart: true });
            await pc.setLocalDescription(offer);
            await this.sendSignal({ description: { type: offer.type, sdp: offer.sdp } });
            await this.pushDiagnostics({ connectionState: 'restarting', iceConnectionState: 'checking', failureReason: '' });
            return true;
        } catch {
            return false;
        } finally {
            this.iceRestartInFlight = false;
        }
    },

    schedulePeerFailureToast(pc = this.peer) {
        if (this.peerFailureToastTimer) {
            window.clearTimeout(this.peerFailureToastTimer);
            this.peerFailureToastTimer = null;
        }
        this.peerFailureToastTimer = window.setTimeout(() => {
            this.peerFailureToastTimer = null;
            const liveConnectionState = String(pc?.connectionState || '').toLowerCase();
            const liveIceState = String(pc?.iceConnectionState || '').toLowerCase();
            if (this.hasRemotePreview() || this.webrtcReady) return;
            if (liveConnectionState === 'failed' || liveIceState === 'failed') {
                this.$store.toastr.error('WebRTC connection failed for this challenge session', 3000);
            }
        }, 1500);
    },

    async syncPeerFromSession() {
        if (!this.hasMediaSession()) return;
        if (!this.mySessionParticipant()?.connected) return;
        if (!this.hasLocalPreview()) return;
        const signaling = this.session?.signaling || {};
        const generation = Number(signaling.generation ?? 0);
        if (generation !== this.signalingGeneration) {
            this.resetPeerConnection({ keepMedia: true });
            this.signalingGeneration = generation;
        }
        const pc = await this.ensurePeerConnection();

        if (this.isOfferer()) {
            if (!pc.localDescription) {
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                await this.sendSignal({ description: { type: offer.type, sdp: offer.sdp } });
                return;
            }
            if (signaling.answer?.sdp && signaling.answer.sdp !== this.appliedAnswerSdp) {
                this.seenAnswerCandidates.clear();
                this.appliedAnswerSdp = signaling.answer.sdp;
            }
            if (signaling.answer?.sdp && String(pc.remoteDescription?.sdp || '') !== signaling.answer.sdp && pc.signalingState === 'have-local-offer') {
                await pc.setRemoteDescription(new RTCSessionDescription(signaling.answer));
                this.remoteDescriptionApplied = true;
            }
            if (this.remoteDescriptionApplied || pc.remoteDescription) {
                for (const candidate of signaling.answerCandidates || []) {
                    const key = JSON.stringify(candidate);
                    if (this.seenAnswerCandidates.has(key)) continue;
                    const added = await this.addRemoteCandidate(candidate, 'answer');
                    this.seenAnswerCandidates.add(key);
                    if (!added) continue;
                }
            }
        } else {
            if (signaling.offer?.sdp && signaling.offer.sdp !== this.appliedOfferSdp) {
                this.seenOfferCandidates.clear();
                this.appliedOfferSdp = signaling.offer.sdp;
                this.answerSent = false;
                this.remoteDescriptionApplied = false;
            }
            if (signaling.offer?.sdp && String(pc.remoteDescription?.sdp || '') !== signaling.offer.sdp && ['stable', 'have-remote-offer'].includes(String(pc.signalingState || ''))) {
                await pc.setRemoteDescription(new RTCSessionDescription(signaling.offer));
                this.remoteDescriptionApplied = true;
            }
            if (this.remoteDescriptionApplied && !pc.localDescription && !this.answerSent) {
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                await this.sendSignal({ description: { type: answer.type, sdp: answer.sdp } });
                this.answerSent = true;
            }
            if (this.remoteDescriptionApplied || pc.remoteDescription) {
                for (const candidate of signaling.offerCandidates || []) {
                    const key = JSON.stringify(candidate);
                    if (this.seenOfferCandidates.has(key)) continue;
                    const added = await this.addRemoteCandidate(candidate, 'offer');
                    this.seenOfferCandidates.add(key);
                    if (!added) continue;
                }
            }
        }
    },

    ensureSignalingLoop() {
        if (this.signalingTimer || !this.hasMediaSession()) return;
        this.signalingTimer = window.setInterval(async () => {
            if (!this.session || !this.mySessionParticipant()?.connected) return;
            try {
                const res = await apiFetch(`/challenges/${this.challenge._id}/session`);
                const data = await res.json();
                if (res.ok && data?.ok) {
                    this.session = data.session || this.session;
                    this.seedFallbackForm();
                    await this.syncPeerFromSession();
                }
            } catch { }
        }, 3000);
    },

    stopSignalingLoop() {
        if (this.signalingTimer) {
            window.clearInterval(this.signalingTimer);
            this.signalingTimer = null;
        }
    },

    teardownMedia() {
        document.body.classList.remove('overflow-hidden');
        applyDedicatedChromeMode(false);
        this.disconnectChallengeEventStream();
        this.stopChallengeScoringHeartbeatLoop();
        this.disconnectEventStream();
        this.stopChallengePollingLoop();
        this.stopSignalingLoop();
        this.stopHeartbeatLoop();
        this.resetPeerConnection({ keepMedia: false });
        this.stopLocalAudioMeter();
        this.stopRemoteAudioMeter();
        this.mediaStatusText = 'Awaiting camera connection';
    },

    playerLabel(player) {
        return playerName(player);
    },

    playerAvatar(row) {
        return row?.player?.imageUrl || '/assets/placeholder-player.png';
    },

    statusClasses(status) {
        return statusMeta(status);
    },

    statusLabel() {
        return String(this.challenge?.status || '').replaceAll('_', ' ');
    },

    modeLabel() {
        return modeMeta(this.challenge?.mode).label;
    },

    modeIcon() {
        return modeMeta(this.challenge?.mode).icon;
    },

    formatDate(value) {
        return fmtDateTime(value);
    },

    isParticipant() {
        const uid = this.currentPlayerId();
        return [String(this.challenge?.challengerPlayerId?._id || this.challenge?.challengerPlayerId || ''), String(this.challenge?.challengedPlayerId?._id || this.challenge?.challengedPlayerId || '')].includes(uid);
    },

    isChallengedPlayer() {
        return String(this.challenge?.challengedPlayerId?._id || this.challenge?.challengedPlayerId || '') === this.currentPlayerId();
    },

    isSubmitter() {
        return String(this.challenge?.workflow?.submittedByPlayerId?._id || this.challenge?.workflow?.submittedByPlayerId || '') === this.currentPlayerId();
    },

    canRespond() {
        const pending = String(this.challenge?.status || '') === 'pending';
        if (!pending) return false;
        if (this.$store.session?.me?.role === 'admin') return true;
        return this.isChallengedPlayer();
    },

    /** Challenger (or anyone who is not the person who must respond) waiting on pending */
    challengePendingWaitingOnOpponent() {
        if (this.$store.session?.me?.role === 'admin') return false;
        return String(this.challenge?.status || '') === 'pending'
            && this.isParticipant()
            && !this.isChallengedPlayer();
    },

    canStart() {
        return this.isParticipant() && ['accepted', 'submitted', 'disputed'].includes(String(this.challenge?.status || ''));
    },

    canEditScore() {
        return this.isParticipant() && ['accepted', 'in_progress', 'submitted', 'disputed'].includes(String(this.challenge?.status || ''));
    },

    canSubmit() {
        return this.isParticipant() && ['accepted', 'in_progress', 'disputed'].includes(String(this.challenge?.status || ''));
    },

    canConfirm() {
        return this.isParticipant() && this.challenge?.status === 'submitted' && !this.isSubmitter();
    },

    canDispute() {
        return this.isParticipant() && ['submitted', 'confirmed'].includes(String(this.challenge?.status || ''));
    },

    challengeResult() {
        return this.challenge?.scoring?.resultText || '0-0';
    },

    highlightPlayer(highlight) {
        return highlight?.playerId ? playerName(highlight.playerId) : 'None yet';
    },

    otherParticipantFromSession(session = this.session) {
        const uid = this.currentPlayerId();
        return (session?.participants || []).find((item) => String(item.playerId?._id || item.playerId || '') !== uid) || null;
    },

    openInterruptionDialog({ key = '', title = '', message = '', allowResume = false, allowDispute = false, forceExit = false } = {}) {
        if (key && this.interruptionDialog?.key === key && this.interruptionDialogOpen) return;
        this.interruptionDialog = { key, title, message, allowResume, allowDispute, forceExit };
        this.interruptionDialogOpen = true;
    },

    closeInterruptionDialog() {
        if (this.interruptionDialog?.forceExit) return;
        this.interruptionDialogOpen = false;
    },

    resumeChallengeFlow() {
        this.interruptionDialogOpen = false;
        if (this.canResumeSession()) {
            this.ensureSessionExists(false).then(() => {
                this.sessionSetupOpen = true;
                this.liveFocusMode = false;
                this.$nextTick(() => {
                    this.$refs?.sessionSetup?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
                });
            }).catch(() => { });
            return;
        }
        this.sessionSetupOpen = true;
        this.liveFocusMode = false;
        this.$nextTick(() => {
            this.$refs?.sessionSetup?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        });
    },

    leaveInterruptedChallenge() {
        window.location.href = '/admin/challenges';
    },

    raiseQuitDispute() {
        const other = this.playerLabel(this.otherParticipantFromSession()?.playerId);
        if (!String(this.disputeReason || '').trim()) {
            this.disputeReason = `${other || 'The other player'} left or ended the challenge unexpectedly before the match could be completed.`;
        }
        this.interruptionDialogOpen = false;
        this.disputeOpen = true;
    },

    evaluateSessionInterruption(previousSession, nextSession, eventType = '') {
        if (!this.isParticipant()) return;
        const prevStatus = String(previousSession?.status || '');
        const nextStatus = String(nextSession?.status || '');
        const prevOther = this.otherParticipantFromSession(previousSession);
        const nextOther = this.otherParticipantFromSession(nextSession);
        const otherName = this.playerLabel(nextOther?.playerId || prevOther?.playerId);

        if (eventType === 'session:ended' || (prevStatus && prevStatus !== 'ended' && nextStatus === 'ended')) {
            if (this.suppressNextSessionEndedDialog) {
                this.suppressNextSessionEndedDialog = false;
                return;
            }
            this.openInterruptionDialog({
                key: `session-ended-${nextSession?._id || this.challenge?._id}`,
                title: 'Challenge Ended',
                message: `${otherName || 'The other player'} ended the live challenge session. You can reconnect and resume, leave the challenge, or raise a dispute if it was unexpected.`,
                allowResume: true,
                allowDispute: true,
            });
            return;
        }

        const prevOtherConnected = !!(prevOther?.connected || prevOther?.cameraEnabled);
        const nextOtherConnected = !!(nextOther?.connected || nextOther?.cameraEnabled);
        const wasActive = ['connecting', 'camera_ready', 'ready', 'live'].includes(prevStatus) || this.hasMediaSession();
        if (wasActive && prevOtherConnected && !nextOtherConnected) {
            this.openInterruptionDialog({
                key: `peer-left-${nextSession?._id || this.challenge?._id}-${nextStatus}`,
                title: 'Opponent Disconnected',
                message: `${otherName || 'The other player'} left the challenge session or lost connection. You can wait and resume camera setup, leave the challenge, or raise a dispute if they quit unexpectedly.`,
                allowResume: true,
                allowDispute: true,
            });
        }
    },

    handleChallengeRemoved() {
        this.openInterruptionDialog({
            key: `challenge-removed-${this.challenge?._id}`,
            title: 'Challenge Removed',
            message: 'This challenge was removed while you were viewing it. You can leave back to the challenges list.',
            allowResume: false,
            allowDispute: false,
            forceExit: true,
        });
    },

    async respond(action) {
        await this.actionRequest(`/challenges/${this.challenge._id}/respond`, { action }, `Challenge ${action}ed`);
    },

    async start() {
        await this.actionRequest(`/challenges/${this.challenge._id}/start`, {}, 'Challenge started');
    },

    async saveScore() {
        this.busy = true;
        try {
            const players = this.scoreRows.map((row) => ({
                playerId: row.playerId,
                legsWon: Number(row.legsWon) || 0,
                pointsScored: Number(row.pointsScored) || 0,
                dartsThrown: Number(row.dartsThrown) || 0,
                highestScore: Number(row.highestScore) || 0,
                tons: csvToNumbers(row.tonsCsv),
                checkouts: csvToStrings(row.checkoutsCsv),
                notes: row.notes || '',
            }));
            const res = await apiFetch(`/challenges/${this.challenge._id}/score`, { method: 'PATCH', body: { players } });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to save score');
            // this.$store.toastr.success('Challenge score updated', 3000);
            await this.reload();
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to save challenge score', 3000);
        } finally {
            this.busy = false;
        }
    },

    async submitChallenge() {
        await this.actionRequest(`/challenges/${this.challenge._id}/submit`, {}, 'Challenge submitted for confirmation');
    },

    async confirmChallenge() {
        await this.actionRequest(`/challenges/${this.challenge._id}/confirm`, {}, 'Challenge result confirmed');
    },

    async submitDispute() {
        await this.actionRequest(`/challenges/${this.challenge._id}/dispute`, { reason: this.disputeReason }, 'Challenge marked as disputed');
        this.disputeOpen = false;
        this.disputeReason = '';
    },

    async actionRequest(url, body, successText) {
        this.busy = true;
        try {
            const res = await apiFetch(url, { method: 'POST', body });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Action failed');
            if (data?.challenge) {
                this.challenge = data.challenge;
                this.scoreRows = this.buildScoreRows();
            }
            this.$store.toastr.success(successText, 3000);
            if (!data?.challenge) await this.reload();
        } catch (e) {
            this.$store.toastr.error(e.message || 'Unable to complete action', 3000);
        } finally {
            this.busy = false;
        }
    },
}));
